/**
 * PORTAIL MEMBRES — /membres  (univers DARK premium, totalement séparé de /app)
 *
 * - Non connecté → connexion sans mot de passe (lien magique + code 6 chiffres).
 * - Connecté → tableau de bord : coachs, horaire/disciplines, progression ceintures,
 *   boutique & promos, billetterie/brackets, annonces du dojo.
 */
import InstallAppBanner from "../components/InstallAppBanner";
import { rememberReturnTo, scrollToMemberHash, memberSectionKey, memberSectionPath } from "../lib/returnTo";
import { useEffect, useState, useCallback } from "react";
import { MemberDrops } from "../components/MemberDrops";
import { Link } from "react-router-dom";
import { memberApi } from "../lib/api";
import { MemberPaymentSituation } from "../components/member/MemberPaymentSituation";
import { toast } from "sonner";
import AcademyMemberSection from "../components/AcademyMemberSection";
import MemberInsuranceSection from "../components/MemberInsuranceSection";
import MemberIntegrationProgress from "../components/MemberIntegrationProgress";
import MemberCardSection from "../components/MemberCardSection";
import MemberSamouraisSection from "../components/MemberSamouraisSection";
import MemberProgramsSection from "../components/MemberProgramsSection";
import MemberFamilySection from "../components/MemberFamilySection";
import MemberFeeButton from "../components/MemberFeeButton";
import MemberBoutique from "../components/MemberBoutique";
import MemberCompleteProfile from "../components/MemberCompleteProfile";
import { MyEquipment } from "../components/equipment/MyEquipment";
import { MySizesAndPreorders } from "../components/equipment/MySizesAndPreorders";
import {
  EnvelopeSimple, ArrowRight, CircleNotch, SignOut, Medal, CalendarBlank, Ticket,
  Storefront, UsersThree, Barbell, Megaphone, ArrowUpRight, PencilSimple, CheckCircle,
  Gift, Copy, ShareNetwork, Trophy, BookOpen, Crown, LinkSimple, CreditCard, Lock, GraduationCap,
  Camera, FilePdf, Info,
} from "@phosphor-icons/react";

const ENSO = "/logos/kaizen_system/generated/kaizen_system_icon_only_enso_K.png";
const LOGO_WHITE = "/logos/master_text_sm.webp";

export default function MembrePortail() {
  const [view, setView] = useState("loading"); // loading | login | portal
  // Raccourcis mobiles : le manifest n'a pas de start_url → le raccourci rouvre la page d'où il a été créé ; la section demandée est ouverte après connexion
  const sectionKey = () => memberSectionKey(window.location.pathname, window.location.hash);
  useEffect(() => { if (view === "portal") scrollToMemberHash(sectionKey()); }, [view]);
  useEffect(() => { if (view === "login" && sectionKey()) rememberReturnTo(memberSectionPath(sectionKey())); }, [view]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("member_token");
    if (!token) { setView("login"); return; }
    memberApi.get("/member/me")
      .then((r) => { setProfile(r.data.member); setView("portal"); })
      .catch(() => { localStorage.removeItem("member_token"); setView("login"); });
  }, []);

  function logout() {
    localStorage.removeItem("member_token");
    localStorage.removeItem("member_profile");
    setProfile(null);
    setView("login");
  }

  if (view === "loading") {
    return (
      <div className="kz-body min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <CircleNotch size={36} className="text-[#D90429] animate-spin" />
      </div>
    );
  }
  if (view === "login") return <MemberLogin onAuthed={(m) => { setProfile(m); setView("portal"); }} />;
  return <MemberDashboard profile={profile} setProfile={setProfile} onLogout={logout} />;
}

/* ─────────────────────────── CONNEXION PASSWORDLESS ─────────────────────── */
function MemberLogin({ onAuthed }) {
  const [step, setStep] = useState("email"); // email | code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");

  async function requestLink(e) {
    e?.preventDefault();
    if (!email.trim()) return toast.error("Entre ton courriel");
    setBusy(true);
    try {
      const r = await memberApi.post("/member/auth/request", { email: email.trim(), origin: window.location.origin, next: window.location.hash.replace("#", "") || null });
      setInfo(r.data.message);
      if (r.data.debug_code) setCode(r.data.debug_code);
      setStep("code");
      toast.success("Lien magique envoyé !");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Erreur d'envoi");
    }
    setBusy(false);
  }

  async function verifyCode(e) {
    e?.preventDefault();
    if (!code.trim()) return toast.error("Entre ton code");
    setBusy(true);
    try {
      const r = await memberApi.post("/member/auth/verify", { email: email.trim(), code: code.trim() });
      localStorage.setItem("member_token", r.data.token);
      localStorage.setItem("member_profile", JSON.stringify(r.data.member));
      toast.success("Bienvenue !");
      onAuthed(r.data.member);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Code invalide");
    }
    setBusy(false);
  }

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  function googleLogin() {
    rememberReturnTo(memberSectionPath(memberSectionKey(window.location.pathname, window.location.hash)));  // la destination du raccourci survit au détour Google
    const redirectUrl = window.location.origin + "/membres";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }

  return (
    <div className="kz-body min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white p-6 relative overflow-hidden" data-testid="membre-login">
      <img src={ENSO} alt="" className="pointer-events-none absolute -right-20 -bottom-20 w-[420px] opacity-[0.05]" />
      <div className="w-full max-w-md p-10 sm:p-12 border border-white/10 bg-black/40 backdrop-blur-xl relative kz-fade-up">
        <img src={LOGO_WHITE} alt="Institut Impact Élite Martial" className="h-12 object-contain" />
        <p className="kz-mono text-[10px] tracking-[0.26em] uppercase text-white/40 mt-5">Espace Membres</p>
        <h1 className="kz-display text-3xl font-medium mt-1">Connexion</h1>

        {step === "email" ? (
          <form onSubmit={requestLink} className="mt-8 space-y-6">
            <div>
              <label className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-2">Ton courriel</label>
              <div className="flex items-center gap-3 border-b border-white/20 focus-within:border-[#D90429] transition-colors pb-2">
                <EnvelopeSimple size={18} className="text-white/40" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  data-testid="login-email-input" placeholder="toi@courriel.com"
                  className="w-full bg-transparent outline-none text-white placeholder-white/30 text-base" />
              </div>
            </div>
            <button type="submit" disabled={busy} data-testid="login-submit-btn"
              className="w-full bg-white text-black py-4 font-bold tracking-wider hover:bg-[#D90429] hover:text-white transition-colors flex items-center justify-center gap-3 disabled:opacity-50">
              {busy ? <CircleNotch size={18} className="animate-spin" /> : <>Recevoir mon lien magique <ArrowRight size={18} weight="bold" /></>}
            </button>
            <p className="kz-body text-xs text-white/40 leading-relaxed">
              Sans mot de passe. Tu reçois un lien sécurisé (et un code) par courriel. Valide 15 minutes.
            </p>
          </form>
        ) : (
          <form onSubmit={verifyCode} className="mt-8 space-y-6">
            {info && <p className="kz-body text-sm text-white/70 bg-white/5 border border-white/10 p-3">{info}</p>}
            <div>
              <label className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-2">Code à 6 chiffres</label>
              <input value={code} onChange={(e) => setCode(e.target.value)} required maxLength={6}
                data-testid="login-code-input" placeholder="000000" inputMode="numeric"
                className="w-full bg-transparent border-b border-white/20 focus:border-[#D90429] outline-none text-white text-3xl tracking-[0.5em] kz-mono py-2 text-center" />
            </div>
            <button type="submit" disabled={busy} data-testid="login-verify-btn"
              className="w-full bg-[#D90429] text-white py-4 font-bold tracking-wider hover:bg-white hover:text-black transition-colors flex items-center justify-center gap-3 disabled:opacity-50">
              {busy ? <CircleNotch size={18} className="animate-spin" /> : <>Entrer dans mon espace <ArrowRight size={18} weight="bold" /></>}
            </button>
            <button type="button" onClick={() => setStep("email")} data-testid="login-back-btn"
              className="w-full kz-mono text-[11px] tracking-[0.2em] uppercase text-white/40 hover:text-white transition-colors">
              ← Changer de courriel
            </button>
          </form>
        )}
        <div className="mt-6 flex items-center gap-4">
          <div className="flex-1 h-px bg-white/10" />
          <span className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/30">ou</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
        <button type="button" onClick={googleLogin} data-testid="member-google-login-btn"
          className="mt-6 w-full bg-white text-black py-4 font-bold tracking-wider hover:bg-white/90 transition-colors flex items-center justify-center gap-3">
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.1 2.8-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z"/>
            <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1C3.4 21.3 7.4 24 12 24z"/>
            <path fill="#FBBC05" d="M5.4 14.4c-.2-.7-.4-1.4-.4-2.4s.1-1.7.4-2.4V6.5H1.4C.5 8.2 0 10 0 12s.5 3.8 1.4 5.5l4-3.1z"/>
            <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4C17.9 1.2 15.2 0 12 0 7.4 0 3.4 2.7 1.4 6.5l4 3.1C6.3 6.8 8.9 4.8 12 4.8z"/>
          </svg>
          Continuer avec Google
        </button>
        <div className="mt-8 pt-6 border-t border-white/10 text-center">
          <p className="kz-body text-xs text-white/40">Pas encore membre ?</p>
          <Link to="/adhesion" data-testid="login-adhesion-link"
            className="inline-block kz-mono text-[11px] tracking-[0.2em] uppercase text-[#D90429] hover:text-white transition-colors mt-1.5">
            S'inscrire en ligne →
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── TABLEAU DE BORD ────────────────────────────── */
/* ─────── MON DOSSIER — vue unifiée (contrat, paiement, accès, équipement) Lot 4 ─────── */

// 🆘 Besoin d'aide ? — SMS ou courriel au dojo en 1 clic
const DOJO_HELP = { phone: "+18195279446", phoneLabel: "819 527-9446", email: "impactkb.bjj@gmail.com" };
function MemberHelpButton({ memberName }) {
  const [open, setOpen] = useState(false);
  const who = memberName ? ` — ${memberName}` : "";
  const smsBody = encodeURIComponent(`Bonjour Institut Impact${who}, j'ai besoin d'aide avec mon Espace membre : `);
  const mailSubject = encodeURIComponent(`Besoin d'aide — Espace membre${who}`);
  return (
    <div className="relative shrink-0" data-testid="member-help">
      <button onClick={() => setOpen((v) => !v)} data-testid="member-help-btn" aria-expanded={open}
        className="kz-mono text-[10px] tracking-[0.18em] uppercase bg-[#D90429] hover:bg-[#b3031f] text-white px-3 py-1.5 transition-colors">🆘 Besoin d'aide ?</button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-72 border border-white/15 bg-[#0C0A09] p-3 shadow-xl z-40" data-testid="member-help-popover">
          <p className="text-white/80 text-xs mb-2">On te répond vite — choisis comment nous joindre :</p>
          <a href={`sms:${DOJO_HELP.phone}?&body=${smsBody}`} data-testid="member-help-sms" className="flex items-center gap-2 border border-white/10 hover:border-[#C9A961] px-3 py-2 text-sm text-white/90 mb-2">💬 Texto au dojo <span className="text-white/50 text-xs ml-auto">{DOJO_HELP.phoneLabel}</span></a>
          <a href={`mailto:${DOJO_HELP.email}?subject=${mailSubject}&body=${smsBody}`} data-testid="member-help-email" className="flex items-center gap-2 border border-white/10 hover:border-[#C9A961] px-3 py-2 text-sm text-white/90 mb-2">✉️ Courriel au dojo <span className="text-white/50 text-xs ml-auto truncate">{DOJO_HELP.email}</span></a>
          <a href={`tel:${DOJO_HELP.phone}`} data-testid="member-help-call" className="flex items-center gap-2 border border-white/10 hover:border-[#C9A961] px-3 py-2 text-sm text-white/90">📞 Appeler <span className="text-white/50 text-xs ml-auto">{DOJO_HELP.phoneLabel}</span></a>
        </div>
      )}
    </div>
  );
}

// ✅ Checklist d'intégration — se coche toute seule à partir des données réelles (GET /member/onboarding)
function MemberOnboardingChecklist({ onEditProfile }) {
  const [d, setD] = useState(null);
  useEffect(() => { memberApi.get("/member/onboarding").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (!d) return null;
  const go = (it) => {
    if (it.key === "profil" && !it.done && it.target === "member-complete-profile" && !document.querySelector('[data-testid="member-complete-profile"]')) return onEditProfile?.();
    document.querySelector(`[data-testid="${it.target}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const pct = Math.round((d.done / d.total) * 100);
  return (
    <div className={`mb-8 border p-5 kz-fade-up ${d.complete ? "border-emerald-500/40 bg-emerald-500/[0.05]" : "border-[#c9a961]/30 bg-[#c9a961]/[0.04]"}`} data-testid="member-next-steps">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="kz-mono text-[10px] tracking-[0.26em] uppercase text-[#c9a961]">Mes prochaines étapes</div>
        <div className="flex items-center gap-3">
          <div className="w-32 h-1.5 bg-white/10 overflow-hidden"><div className="h-full bg-[#c9a961] transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
          <span className="kz-mono text-[11px] text-white/70" data-testid="checklist-progress">{d.done}/{d.total} {d.complete ? "· tout est prêt ✔" : "complétées"}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {d.items.map((it) => (
          <button key={it.key} onClick={() => go(it)} data-testid={`next-step-${it.key}`} data-done={it.done ? "true" : "false"}
            className={`flex items-start gap-3 border p-3.5 text-left transition-colors ${it.done ? "border-emerald-500/30 bg-emerald-500/[0.06]" : "border-white/10 hover:border-[#c9a961]/60 bg-white/[0.02] hover:bg-[#c9a961]/[0.06]"}`}>
            <span className={`mt-0.5 w-5 h-5 shrink-0 rounded-full border flex items-center justify-center text-[11px] ${it.done ? "bg-emerald-500 border-emerald-500 text-black" : "border-white/30 text-transparent"}`}>✔</span>
            <span>
              <span className={`kz-body text-sm block ${it.done ? "text-white/60 line-through decoration-white/30" : "text-white/90"}`}>{it.label}</span>
              <span className="text-[11px] text-white/45 block mt-0.5">{it.detail}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// 💳 Hub « Mes paiements » — une seule section : résumé + sous-blocs (frais · carte · échéancier · famille · PKH)
function MemberPaymentsHub({ children }) {
  const [hub, setHub] = useState(null);
  useEffect(() => { memberApi.get("/member/hub").then((r) => setHub(r.data)).catch(() => setHub(false)); }, []);
  const p = hub?.payment;
  const card = hub?.card;
  return (
    <section className="border border-white/10 bg-white/[0.02] p-6 mb-8 kz-fade-up" data-testid="member-payments-hub">
      <p className="kz-mono text-[10px] tracking-[0.26em] uppercase text-[#C9A961]">Mes paiements</p>
      <h2 className="kz-display text-2xl mt-1">Abonnement, carte et échéancier — au même endroit</h2>
      {hub && p && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 text-sm" data-testid="payments-summary">
          <div className="border border-white/10 p-3"><p className="kz-mono text-[10px] tracking-[0.16em] uppercase text-white/40">Statut</p>
            <p className={`mt-1 font-medium ${p.en_retard ? "text-[#ff6b6b]" : "text-emerald-300"}`} data-testid="payments-status">{p.en_retard ? "Paiement en retard" : (p.stripe_subscription_status === "active" ? "Abonnement actif" : "À jour")}</p></div>
          <div className="border border-white/10 p-3"><p className="kz-mono text-[10px] tracking-[0.16em] uppercase text-white/40">Prochain paiement</p>
            <p className="mt-1 text-white/85" data-testid="payments-next">{p.prochain_paiement_du || "—"}{p.tarif ? ` · ${Number(p.tarif).toFixed(2)} $` : ""}</p></div>
          <div className="border border-white/10 p-3"><p className="kz-mono text-[10px] tracking-[0.16em] uppercase text-white/40">Carte</p>
            <p className="mt-1 text-white/85" data-testid="payments-card">{card?.has_card ? `${card.brand || "Carte"} •••• ${card.last4}${card.auto_pay ? " · auto" : ""}` : "Aucune carte enregistrée"}</p></div>
        </div>
      )}
      <div className="mt-4 divide-y divide-white/10 [&>section]:mb-0 [&>section]:border-0 [&>section]:bg-transparent [&>section]:px-0 [&>div]:mb-0 [&>section]:py-5">{children}</div>
    </section>
  );
}

// 🥋 Groupe pédagogique + uniforme requis — affiché UNIQUEMENT si le Kru a validé l'entrée de groupe
function MemberPedagogicalGroup() {
  const [g, setG] = useState(null);
  useEffect(() => { memberApi.get("/member/pedagogical-group").then((r) => setG(r.data.groups || [])).catch(() => setG([])); }, []);
  if (!g || !g.length) return null;
  return (
    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dossier-pedagogical-groups">
      {g.map((x) => (
        <div key={x.profile_id} className="border border-sky-500/40 bg-sky-500/[0.06] p-4" data-testid={`dossier-group-${x.profile_id}`}>
          <p className="kz-mono text-[10px] tracking-[0.16em] uppercase text-sky-300">Mon groupe pédagogique · {x.discipline_label}</p>
          <p className="text-white/90 mt-2 text-sm font-medium" data-testid="dossier-group-name"><Medal size={14} className="inline mr-1 text-sky-300" /> {x.group_name} <span className="text-white/50">(niveau {x.level})</span></p>
          <p className="text-white/60 text-xs mt-1">Ceinture réelle : {x.belt}{x.student_name ? ` · ${x.student_name}` : ""}</p>
          {x.uniform && <p className="text-white/85 text-sm mt-2" data-testid="dossier-group-uniform">🥋 Uniforme {x.uniform_required ? "requis" : "recommandé"} : <b>{x.uniform}</b></p>}
          {x.products?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {x.products.map((pr) => <button key={pr.id} onClick={() => document.querySelector('[data-testid="member-boutique"]')?.scrollIntoView({ behavior: "smooth", block: "start" })} className="text-[11px] border border-[#C9A961]/50 text-[#C9A961] px-2 py-0.5 hover:bg-[#C9A961]/10" data-testid={`dossier-group-product-${pr.id}`}>🛍 {pr.name}{pr.member_price ? ` · ${Number(pr.member_price).toFixed(2)} $` : ""}</button>)}
            </div>
          )}
          {x.is_hinge && x.next_group && <p className="text-amber-200 text-xs mt-2">Ceinture charnière — prochaine étape : {x.next_group} (après validation du Kru).</p>}
          <p className="text-[10px] text-white/40 mt-2">Validé par le Kru le {x.validated_at?.slice(0, 10)}. Les promotions de ceinture restent toujours validées au dojo.</p>
        </div>
      ))}
    </div>
  );
}

function MemberDossier() {
  const [hub, setHub] = useState(null);
  useEffect(() => {
    memberApi.get("/member/hub").then((r) => setHub(r.data)).catch(() => setHub(false));
  }, []);
  if (hub === null) return null;
  if (hub === false) return null;

  const c = hub.contract;
  const p = hub.payment;
  const a = hub.attendance;
  const payOk = p && (p.stripe_subscription_status === "active" || !p.en_retard);
  const payBadge = !p ? { t: "Aucun contrat", cls: "bg-white/10 text-white/60" }
    : p.en_retard ? { t: "Paiement en retard", cls: "bg-[#D90429]/20 text-[#ff6b6b]" }
    : { t: p.stripe_subscription_status === "active" ? "Abonnement actif" : "À jour", cls: "bg-emerald-500/15 text-emerald-300" };

  return (
    <section className="border border-white/10 bg-white/[0.02] p-6 mb-8 kz-fade-up" data-testid="member-dossier">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <BookOpen size={22} className="text-[#D90429]" weight="duotone" />
          <h3 className="kz-display text-xl">Mon dossier</h3>
        </div>
        <span className={`kz-mono text-[10px] tracking-[0.14em] uppercase px-3 py-1 ${payBadge.cls}`} data-testid="dossier-pay-badge">{payBadge.t}</span>
      </div>

      {!hub.has_contract ? (
        <p className="text-white/55 text-sm mt-4" data-testid="dossier-no-contract">
          Aucun contrat relié à ton compte pour l'instant. Contacte la réception ou complète ton adhésion.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
          {/* Contrat */}
          <div className="border border-white/10 p-4" data-testid="dossier-contract">
            <p className="kz-mono text-[10px] tracking-[0.16em] uppercase text-white/40">Contrat</p>
            <p className="text-white/90 mt-2 text-sm"><Barbell size={14} className="inline mr-1 text-[#D90429]" /> {c.discipline_label}</p>
            <p className="text-white/70 text-sm mt-1"><Medal size={14} className="inline mr-1 text-[#D90429]" /> Ceinture {c.niveau_ceinture}</p>
            {p?.date_debut && <p className="text-white/50 text-xs mt-2">Du {p.date_debut}{p.date_fin ? ` au ${p.date_fin}` : " (en cours)"}</p>}
          </div>
          {/* Accès hebdo */}
          <div className="border border-white/10 p-4" data-testid="dossier-access">
            <p className="kz-mono text-[10px] tracking-[0.16em] uppercase text-white/40">Accès cette semaine</p>
            {a.unlimited ? (
              <p className="kz-display text-2xl text-emerald-300 mt-2">Illimité</p>
            ) : (
              <>
                <p className="kz-display text-2xl text-white mt-2" data-testid="dossier-access-count">{a.used_this_week} / {a.allowed_this_week}</p>
                <p className="text-white/50 text-xs mt-1">{a.remaining_this_week} accès restant(s) ({a.week_start} → {a.week_end})</p>
              </>
            )}
          </div>
          {/* Paiement / Stripe */}
          <div className="border border-white/10 p-4" data-testid="dossier-payment">
            <p className="kz-mono text-[10px] tracking-[0.16em] uppercase text-white/40">Facturation</p>
            <p className="text-white/90 mt-2 text-sm"><CreditCard size={14} className="inline mr-1 text-[#D90429]" /> {p?.tarif ? `${Number(p.tarif).toLocaleString("fr-CA")} $` : "—"} {p?.billing_cycle === "4_semaines" ? "/ 4 sem." : ""}</p>
            <p className="text-white/60 text-xs mt-1">{hub.card.has_card ? `Carte ${hub.card.brand || ""} ••${hub.card.last4 || ""}${hub.card.auto_pay ? " · Auto-pay ✓" : ""}` : "Aucune carte enregistrée"}</p>
          </div>
        </div>
      )}

      {/* Équipement + boutique */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div className="border border-white/10 p-4" data-testid="dossier-equipment">
          <p className="kz-mono text-[10px] tracking-[0.16em] uppercase text-white/40">Équipement requis</p>
          <ul className="mt-2 space-y-1">
            {hub.equipment.map((e, i) => (
              <li key={i} className="text-white/75 text-sm flex items-center gap-2"><CheckCircle size={14} className="text-emerald-400" /> {e}</li>
            ))}
          </ul>
          <Link to="/store" data-testid="dossier-store-btn" className="inline-flex items-center gap-2 mt-3 bg-[#D90429] text-white px-4 py-2 text-sm font-bold hover:bg-white hover:text-black transition-colors">
            <Storefront size={16} weight="fill" /> Boutique — m'équiper
          </Link>
        </div>
        <div className="border border-white/10 p-4" data-testid="dossier-rules">
          <p className="kz-mono text-[10px] tracking-[0.16em] uppercase text-white/40">Règles du dojo</p>
          <ul className="mt-2 space-y-1">
            {hub.rules.map((r, i) => (
              <li key={i} className="text-white/65 text-[13px] leading-snug">• {r}</li>
            ))}
          </ul>
        </div>
      </div>
      <MemberPedagogicalGroup />
    </section>
  );
}


function MemberAttendanceBadge() {
  const [data, setData] = useState(null); // {threshold, thisMonth, badges, totalBadges}

  useEffect(() => {
    memberApi.get("/training/completions/me/attendance")
      .then((r) => setData(r.data))
      .catch(() => setData(false));
  }, []);

  if (!data) return null;
  const { threshold, thisMonth, totalBadges } = data;
  const done = thisMonth.completed;
  const pct = Math.min(100, Math.round((done / threshold) * 100));
  const earned = thisMonth.badgeEarned;

  return (
    <section className="border border-white/10 p-7 kz-fade-up" data-testid="member-attendance-badge">
      <ModuleTitle icon={Medal} over="Assiduité">Badge du mois</ModuleTitle>
      <div className="mt-5 flex items-center gap-5">
        <div
          className={`relative w-20 h-20 rounded-full flex items-center justify-center shrink-0 border-2 ${
            earned ? "border-[#c9a961] bg-[#c9a961]/10" : "border-white/15 bg-white/[0.03]"
          }`}
          data-testid="attendance-badge-medal"
        >
          <Medal size={38} weight={earned ? "fill" : "regular"} className={earned ? "text-[#c9a961]" : "text-white/35"} />
          {earned && (
            <span className="absolute -top-1 -right-1 text-[10px] bg-[#c9a961] text-black font-bold rounded-full px-1.5 py-0.5">✓</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          {earned ? (
            <p className="text-[#c9a961] font-semibold text-sm" data-testid="attendance-badge-earned">
              🎉 Badge d'assiduité débloqué ce mois-ci !
            </p>
          ) : (
            <p className="text-white/70 text-sm" data-testid="attendance-badge-progress">
              Complète <b className="text-white">{thisMonth.remaining}</b> entraînement{thisMonth.remaining > 1 ? "s" : ""} guidé{thisMonth.remaining > 1 ? "s" : ""} de plus ce mois-ci pour débloquer ton badge.
            </p>
          )}
          <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500"
              style={{ width: `${pct}%`, background: earned ? "#c9a961" : "#D90429" }} />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[11px] text-white/45 kz-mono">
            <span>{done} / {threshold} ce mois</span>
            {totalBadges > 0 && (
              <span className="inline-flex items-center gap-1 text-[#c9a961]" data-testid="attendance-badge-total">
                <Trophy size={12} weight="fill" /> {totalBadges} badge{totalBadges > 1 ? "s" : ""} gagné{totalBadges > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}


function MemberCheckin() {
  const [opts, setOpts] = useState(null); // {options, geofence_set, dojo_name}
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {ok, status, message}

  useEffect(() => {
    memberApi.get("/member/checkin/options")
      .then((r) => { setOpts(r.data); if ((r.data.options || []).length === 1) setSelected(r.data.options[0].contract_id); })
      .catch(() => setOpts({ options: [] }));
  }, []);

  const getGeo = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { timeout: 6000, maximumAge: 30000, enableHighAccuracy: true }
    );
  });

  const doCheckin = async () => {
    if (busy) return;
    setBusy(true); setResult(null);
    try {
      const geo = await getGeo();
      const body = {};
      if (selected) body.contract_id = selected;
      if (geo) { body.lat = geo.lat; body.lng = geo.lng; }
      const { data } = await memberApi.post("/member/checkin", body);
      setResult(data);
      if (data.ok && data.status !== "doublon") toast.success(data.message);
      else if (data.status === "doublon") toast.info(data.message);
      else toast.error(data.message);
    } catch (e) {
      const msg = e.response?.data?.detail || "Check-in impossible";
      setResult({ ok: false, status: "refuse", message: msg });
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (!opts || (opts.options || []).length === 0) return null;

  const RES_COLOR = {
    present: "text-emerald-400", essai: "text-cyan-400", present_retard: "text-amber-400",
    doublon: "text-blue-400", refuse: "text-[#D90429]",
  };

  return (
    <section className="border border-[#D90429]/40 bg-gradient-to-r from-[#D90429]/10 to-transparent p-6 mb-8 kz-fade-up" data-testid="member-checkin">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="kz-mono text-[10px] tracking-[0.26em] uppercase text-white/40">Check-in dojo</p>
          <h2 className="kz-display text-2xl mt-1">Tu es au {opts.dojo_name || "dojo"} ?</h2>
          <p className="text-white/50 text-sm mt-1">
            Enregistre ta présence en un clic.{opts.geofence_set ? " Ta position est vérifiée." : ""}
          </p>
        </div>
        <div className="flex flex-col gap-2 md:items-end">
          {opts.options.length > 1 && (
            <select
              data-testid="checkin-member-select"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="bg-[#0A0A0A] border border-white/15 px-3 py-2 text-sm focus:border-[#D90429] focus:outline-none"
            >
              <option value="">Choisir l'élève…</option>
              {opts.options.map((o) => (
                <option key={o.contract_id} value={o.contract_id}>{o.full_name}</option>
              ))}
            </select>
          )}
          <button
            data-testid="member-checkin-btn"
            onClick={doCheckin}
            disabled={busy || (opts.options.length > 1 && !selected)}
            className="bg-[#D90429] hover:bg-white hover:text-[#0A0A0A] disabled:opacity-40 px-6 py-3 flex items-center gap-2 font-medium transition-colors"
          >
            {busy ? <CircleNotch size={18} className="animate-spin" /> : <CheckCircle size={18} weight="fill" />}
            {busy ? "Localisation…" : "Je suis arrivé · Check-in"}
          </button>
        </div>
      </div>
      {result && (
        <div className={`mt-4 pt-4 border-t border-white/10 ${RES_COLOR[result.status] || "text-white"}`} data-testid="checkin-result">
          <CheckCircle size={16} weight="fill" className="inline mr-2" />
          {result.message}
        </div>
      )}
    </section>
  );
}

/* ─────────── Carte de paiement — enregistrement UNE fois (Phase 3c) ─────────── */
function MemberPkhAddon() {
  const [elig, setElig] = useState(null); // {price_cad, places_left, already_enrolled}
  const [busy, setBusy] = useState(false);

  const load = () => memberApi.get("/member/pkh/eligibility")
    .then((r) => setElig(r.data)).catch(() => setElig(false));

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sess = params.get("pkh_session");
    if (sess) {
      memberApi.get(`/member/pkh/status/${sess}`)
        .then((r) => { if (r.data.payment_status === "paid") toast.success("PowerKick Her ajouté à ton forfait ✓ On se voit sur le ring 🥊"); })
        .catch(() => {})
        .finally(() => { window.history.replaceState({}, "", window.location.pathname); load(); });
    } else if (params.get("pkh_cancelled")) {
      window.history.replaceState({}, "", window.location.pathname);
      load();
    } else { load(); }
  }, []);

  const addPkh = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await memberApi.post("/member/pkh/checkout", { origin: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir le paiement");
      setBusy(false);
    }
  };

  if (!elig) return null;
  if (!elig.already_enrolled) return null; // 🙈 masqué pour les membres non inscrits (Ralph 29-09)
  if (elig.already_enrolled) {
    return (
      <section className="border border-[#C9A961]/40 bg-[#C9A961]/[0.06] p-6 mb-8 kz-fade-up" data-testid="member-pkh-addon">
        <div className="flex items-center gap-2 text-[#C9A961]">
          <CheckCircle size={18} weight="fill" />
          <span className="kz-mono text-[10px] tracking-[0.18em] uppercase">PowerKick Her</span>
        </div>
        <h2 className="kz-display text-2xl mt-1" data-testid="pkh-addon-enrolled">Tu es inscrite à PowerKick Her ✓</h2>
        <p className="text-white/55 text-sm mt-2">Ta session de 12 semaines · 100 % femmes est confirmée. On se voit sur le ring 🥊</p>
      </section>
    );
  }
  const full = (elig.places_left ?? 0) <= 0;
  return (
    <section className="border border-[#D90429]/40 bg-gradient-to-br from-[#D90429]/[0.08] to-transparent p-6 mb-8 kz-fade-up" data-testid="member-pkh-addon">
      <span className="kz-mono text-[10px] tracking-[0.2em] uppercase text-[#D90429]">🥊 100 % femmes · 12 semaines</span>
      <h2 className="kz-display text-2xl mt-1">Ajoute PowerKick Her à ton forfait</h2>
      <p className="text-white/55 text-sm mt-2 max-w-xl">Le kickboxing au féminin, en groupe 100 % femmes. Paiement unique — <b className="text-white">aucun frais d'inscription</b> puisque tu es déjà membre active.</p>
      <div className="flex flex-wrap items-end gap-4 mt-4">
        <div className="kz-display text-3xl text-[#D90429]" data-testid="pkh-addon-price">
          {Number(elig.price_cad).toLocaleString("fr-CA")} $
          <span className="kz-body text-white/40 text-sm"> + tx · session 12 sem.</span>
        </div>
        {!full && elig.places_left != null && (
          <span className="kz-mono text-xs text-[#C9A961]" data-testid="pkh-addon-places">Plus que {elig.places_left} place{elig.places_left > 1 ? "s" : ""}</span>
        )}
      </div>
      {full ? (
        <div className="mt-4 border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-300 text-sm" data-testid="pkh-addon-full">
          Session complète — inscris-toi sur la liste d'attente depuis la <Link to="/adhesion" className="underline">page d'adhésion</Link>.
        </div>
      ) : (
        <button onClick={addPkh} disabled={busy} data-testid="pkh-addon-btn"
          className="mt-4 inline-flex items-center gap-2 bg-[#D90429] text-white px-6 py-3 text-sm font-bold hover:bg-white hover:text-[#D90429] transition-colors disabled:opacity-60">
          {busy ? <CircleNotch size={18} className="animate-spin" /> : <ArrowRight size={18} weight="bold" />}
          Ajouter PowerKick Her — payer en ligne
        </button>
      )}
    </section>
  );
}

function MemberPaymentCard() {
  const [card, setCard] = useState(null); // {has_card, locked, brand, last4, ...}
  const [busy, setBusy] = useState(false);

  const load = () => memberApi.get("/member/payment/card")
    .then((r) => setCard(r.data)).catch(() => setCard({ has_card: false }));

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const evalPaid = params.get("eval_paid");
    if (evalPaid && params.get("session_id")) {
      memberApi.post(`/member/academy/evaluation/${evalPaid}/pay/verify?session_id=${encodeURIComponent(params.get("session_id"))}`)
        .then((r) => { if (r.data.status === "paye") toast.success("Évaluation payée ✓ — merci ! Le Kru confirme la date."); else toast.message("Paiement en cours de confirmation…"); })
        .catch(() => toast.error("Paiement non confirmé — réessaie depuis ton carnet."))
        .finally(() => window.history.replaceState({}, "", window.location.pathname));
    } else if (params.get("eval_cancelled")) {
      toast.message("Paiement annulé — tu peux réessayer depuis ton carnet.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    const sess = params.get("card_session");
    if (sess) {
      memberApi.get(`/member/payment/setup-status/${sess}`)
        .then((r) => { if (r.data.status === "saved") toast.success(`Carte enregistrée ✓ (•••• ${r.data.last4})`); })
        .catch(() => {})
        .finally(() => { window.history.replaceState({}, "", window.location.pathname); load(); });
    } else if (params.get("card_cancelled")) {
      window.history.replaceState({}, "", window.location.pathname);
      load();
    } else { load(); }
  }, []);

  const saveCard = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await memberApi.post("/member/payment/setup-session", { origin: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir le paiement");
      setBusy(false);
    }
  };

  const toggleAutoPay = async () => {
    try {
      const next = !card?.auto_pay;
      await memberApi.post("/member/payment/auto-pay", { enabled: next });
      toast.success(next ? "✅ Prélèvement automatique activé" : "Prélèvement automatique désactivé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };

  if (!card) return null;

  return (
    <section className="border border-white/10 bg-white/[0.02] p-6 mb-8 kz-fade-up" data-testid="member-payment-card">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="kz-mono text-[10px] tracking-[0.26em] uppercase text-white/40 flex items-center gap-2">
            <CreditCard size={14} /> Mode de paiement
          </p>
          {card.has_card ? (
            <>
              <h2 className="kz-display text-2xl mt-1" data-testid="card-on-file">
                {String(card.brand || "Carte").toUpperCase()} •••• {card.last4}
              </h2>
              <p className="text-white/50 text-sm mt-1 flex items-center gap-1.5">
                <Lock size={13} /> Expire {String(card.exp_month).padStart(2, "0")}/{card.exp_year} · Pour modifier ta carte : bouton « Changer la carte » dans « Ma famille » (payeurs) ou contacte l'administration du dojo.
              </p>
            </>
          ) : (
            <>
              <h2 className="kz-display text-2xl mt-1">Enregistre ta carte</h2>
              <p className="text-white/50 text-sm mt-1">
                Ajoute ta carte une seule fois pour des paiements sans tracas. Sécurisé par Stripe — aucune donnée de carte n'est conservée par le dojo.
              </p>
            </>
          )}
        </div>
        {!card.has_card && (
          <button
            data-testid="save-card-btn"
            onClick={saveCard}
            disabled={busy}
            className="bg-[#D90429] hover:bg-white hover:text-[#0A0A0A] disabled:opacity-40 px-6 py-3 flex items-center gap-2 font-medium transition-colors whitespace-nowrap"
          >
            {busy ? <CircleNotch size={18} className="animate-spin" /> : <CreditCard size={18} weight="fill" />}
            {busy ? "Ouverture…" : "Enregistrer ma carte"}
          </button>
        )}
        {card.has_card && (
          <div className="flex items-center gap-2 text-emerald-400 text-sm" data-testid="card-locked-badge">
            <CheckCircle size={18} weight="fill" /> Carte au dossier
          </div>
        )}
      </div>

      {card.has_card && (
        <div className="mt-5 pt-4 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="member-autopay-row">
          <div>
            <p className="text-white font-medium flex items-center gap-2">
              Prélèvement automatique
              {card.auto_pay
                ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">ACTIVÉ</span>
                : <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60">désactivé</span>}
            </p>
            <p className="text-white/50 text-sm mt-0.5">
              {card.auto_pay
                ? "Tes versements sont prélevés automatiquement sur ta carte à chaque échéance."
                : "Active-le pour que chaque versement soit prélevé automatiquement à échéance — plus rien à gérer."}
            </p>
          </div>
          <button
            data-testid="member-toggle-autopay"
            onClick={toggleAutoPay}
            className={`px-5 py-2.5 font-medium transition-colors whitespace-nowrap ${card.auto_pay ? "bg-white/10 hover:bg-white/20 text-white" : "bg-emerald-600 hover:bg-emerald-500 text-white"}`}
          >
            {card.auto_pay ? "Désactiver" : "Activer l'auto-paiement"}
          </button>
        </div>
      )}
    </section>
  );
}


/* ─────────── Mon échéancier — le membre voit et paie ses versements ─────────── */
function MemberBillingSchedule() {
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(null);

  const load = () => memberApi.get("/member/billing")
    .then((r) => setData(r.data.contracts || []))
    .catch(() => setData([]));

  useEffect(() => { load(); }, []);

  const pay = async (pid) => {
    setPaying(pid);
    try {
      const r = await memberApi.post("/member/billing/pay", { payment_id: pid });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Paiement impossible");
      setPaying(null);
    }
  };

  const fmt = (n) => `${Number(n || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;

  if (!data || data.length === 0) return null;

  return (
    <section className="border border-white/10 bg-white/[0.02] p-6 mb-8 kz-fade-up" data-testid="member-billing-section">
      <p className="kz-mono text-[10px] tracking-[0.26em] uppercase text-white/40 flex items-center gap-2 mb-4">
        <CreditCard size={14} /> Mon échéancier de paiements
      </p>
      <div className="space-y-6">
        {data.map((c) => (
          <div key={c.contract_id} data-testid={`billing-contract-${c.contract_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <h3 className="kz-display text-xl">{c.member_name} <span className="text-white/40 text-sm">· {c.discipline}</span></h3>
              {c.summary && (
                <div className="text-sm text-white/60">
                  Payé <span className="text-emerald-400">{fmt(c.summary.amount_paid)}</span> ·
                  Solde <span className="text-amber-400">{fmt(c.summary.balance)}</span> ·
                  {c.summary.installments_paid}/{c.summary.installments_total} versements
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              {c.schedule.map((p) => (
                <div key={p.id} className="flex items-center gap-3 border border-white/10 px-3 py-2.5" data-testid={`billing-line-${p.id}`}>
                  <span className="text-white/30 w-6 text-sm">#{p.installment_index}</span>
                  <span className="text-white/70 text-sm w-24">{p.due_date}</span>
                  <span className="font-medium">{fmt(p.amount)}</span>
                  {p.status === "paid" ? (
                    <span className="ml-auto flex items-center gap-1.5 text-emerald-400 text-sm" data-testid={`billing-paid-${p.id}`}>
                      <CheckCircle size={16} weight="fill" /> Payé {p.receipt_no || ""}
                    </span>
                  ) : (
                    <button onClick={() => pay(p.id)} disabled={paying === p.id} data-testid={`billing-pay-${p.id}`}
                      className="ml-auto bg-[#D90429] hover:bg-white hover:text-[#0A0A0A] disabled:opacity-40 px-4 py-1.5 text-sm font-medium transition-colors flex items-center gap-1.5">
                      {paying === p.id ? <CircleNotch size={15} className="animate-spin" /> : <CreditCard size={15} weight="fill" />}
                      {paying === p.id ? "Ouverture…" : "Payer maintenant"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-white/30 text-xs mt-4">Paiement sécurisé par Stripe. Ton reçu est généré automatiquement après le paiement.</p>
    </section>
  );
}


// 🥋 PREMIER MOIS — checklist équipement côté membre (Obligatoire/Recommandé/Avancé)
function MemberFirstMonth() {
  const [fm, setFm] = useState(null);
  const [busy, setBusy] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = () => memberApi.get("/member/first-month")
    .then((r) => setFm(r.data)).catch(() => setFm(false));
  useEffect(() => { load(); }, []);

  if (!fm || fm === false || !fm.has_contract) return null;

  const items = fm.items || {};
  const st = (key) => items[key]?.status || "a_faire";
  const isDone = (key) => ["complete_membre", "verifie"].includes(st(key));
  const isVerified = (key) => st(key) === "verifie";
  const proofOf = (key) => items[key]?.proof_url;
  const pct = fm.progress?.total ? Math.round((fm.progress.done / fm.progress.total) * 100) : 0;
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const toggle = async (key) => {
    if (isVerified(key)) return; // verrouillé par le coach
    setBusy(key);
    const next = isDone(key) ? "a_faire" : "complete_membre";
    try {
      await memberApi.post("/member/first-month/item", { item_key: key, status: next });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de mettre à jour.");
    } finally { setBusy(null); }
  };

  const uploadProof = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const form = new FormData();
      form.append("item_key", key);
      form.append("file", file);
      await memberApi.post("/member/first-month/proof", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Preuve téléversée · en attente de validation du coach 📷");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Téléversement impossible.");
    } finally { setUploading(null); }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await memberApi.get("/member/first-month/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "checklist-premier-mois.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Impossible de générer le PDF.");
    } finally { setPdfBusy(false); }
  };

  const Row = ({ itemKey, label, cle }) => (
    <div
      data-testid={`fm-row-${itemKey}`}
      className={`w-full flex items-center gap-3 border px-3 py-2.5 transition-colors ${
        isDone(itemKey)
          ? "border-emerald-500/40 bg-emerald-500/10"
          : "border-white/10 bg-white/[0.02] hover:border-white/30"
      }`}
    >
      <button
        type="button"
        data-testid={`fm-item-${itemKey}`}
        onClick={() => toggle(itemKey)}
        disabled={busy === itemKey || isVerified(itemKey)}
        className="flex items-center gap-3 flex-1 text-left disabled:cursor-default"
      >
        {busy === itemKey
          ? <CircleNotch size={18} className="animate-spin text-white/50 shrink-0" />
          : isDone(itemKey)
            ? <CheckCircle size={18} weight="fill" className="text-emerald-400 shrink-0" />
            : <span className="w-[18px] h-[18px] border border-white/30 shrink-0 rounded-[2px]" />}
        <span className={`text-sm ${isDone(itemKey) ? "text-emerald-100" : "text-white/80"}`}>
          {label}{cle && <span className="ml-1.5 text-[#c9a961]" title="Obligatoire partout">★</span>}
        </span>
      </button>

      {proofOf(itemKey) && (
        <a href={`${apiBase}${proofOf(itemKey)}`} target="_blank" rel="noreferrer"
           data-testid={`fm-proof-link-${itemKey}`}
           className="text-[10px] kz-mono tracking-wider text-emerald-300/90 hover:text-emerald-200 uppercase">📷 Preuve</a>
      )}
      {isVerified(itemKey)
        ? <span className="text-[10px] kz-mono tracking-wider text-emerald-400/90 uppercase shrink-0">✓ Validé coach</span>
        : (
          <label data-testid={`fm-upload-${itemKey}`}
            className="shrink-0 cursor-pointer text-white/40 hover:text-[#c9a961] transition-colors" title="Ajouter une photo / un reçu">
            {uploading === itemKey ? <CircleNotch size={16} className="animate-spin" /> : <Camera size={16} />}
            <input type="file" accept="image/*,.pdf" className="hidden"
              onChange={(e) => { uploadProof(itemKey, e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        )}
    </div>
  );

  const Block = ({ title, color, list, prefix }) => (
    list && list.length > 0 ? (
      <div className="mb-5">
        <p className="kz-mono text-[10px] tracking-[0.22em] uppercase mb-2" style={{ color }}>{title}</p>
        <div className="space-y-1.5">
          {list.map((it) => <Row key={it.key} itemKey={`${prefix}${it.key}`} label={it.label} cle={it.cle} />)}
        </div>
      </div>
    ) : null
  );

  const def = fm.definition || {};
  const fee = fm.fee || {};
  const ageNotes = fm.age_notes || [];

  return (
    <section className="border border-[#c9a961]/40 bg-gradient-to-br from-[#c9a961]/[0.06] to-transparent p-6 mb-8 kz-fade-up" data-testid="member-first-month">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="kz-mono text-[10px] tracking-[0.26em] uppercase text-[#c9a961] flex items-center gap-2">
          <Barbell size={14} /> Mon premier mois · {def.discipline_label || ""}
        </p>
        <div className="flex items-center gap-3">
          <span className="text-sm text-white/60" data-testid="fm-progress-label">
            {fm.progress?.done || 0}/{fm.progress?.total || 0} complété{(fm.progress?.done || 0) > 1 ? "s" : ""}
          </span>
          <button type="button" onClick={downloadPdf} disabled={pdfBusy} data-testid="fm-pdf-btn"
            className="flex items-center gap-1.5 border border-white/20 hover:border-[#c9a961] hover:text-[#c9a961] px-3 py-1.5 text-xs transition-colors disabled:opacity-40">
            {pdfBusy ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} />}
            Imprimer / PDF
          </button>
        </div>
      </div>

      {/* Barre de progression */}
      <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-1">
        <div className="h-full bg-gradient-to-r from-[#c9a961] to-emerald-400 transition-all duration-500"
          style={{ width: `${pct}%` }} data-testid="fm-progress-bar" />
      </div>
      <p className="text-white/40 text-xs mb-5">{pct}% — coche tes achats et ajoute une photo/reçu 📷. Le coach validera ensuite ✓.</p>

      {/* Frais d'inscription */}
      {fee.applicable && fee.amount > 0 && (
        <div className={`mb-5 border px-4 py-3 flex items-center gap-3 ${
          fee.status === "paye" ? "border-emerald-500/40 bg-emerald-500/10" : "border-amber-500/40 bg-amber-500/10"
        }`} data-testid="fm-fee">
          {fee.status === "paye"
            ? <CheckCircle size={20} weight="fill" className="text-emerald-400 shrink-0" />
            : <CreditCard size={20} className="text-amber-400 shrink-0" />}
          <div className="text-sm">
            <b>Frais d'inscription : {Number(fee.amount).toFixed(2)} $</b>{" "}
            {fee.status === "paye"
              ? <span className="text-emerald-300">— payés ✓</span>
              : <span className="text-amber-300">— à régler : utilise le bouton « Payer maintenant » en haut de la page (lien Stripe direct)</span>}
          </div>
        </div>
      )}

      {/* Adaptations selon l'âge */}
      {ageNotes.length > 0 && (
        <div className="mb-5 border border-[#c9a961]/30 bg-[#c9a961]/[0.06] px-4 py-3" data-testid="fm-age-notes">
          <p className="kz-mono text-[10px] tracking-[0.22em] uppercase text-[#c9a961] flex items-center gap-2 mb-2">
            <Info size={13} /> Recommandations selon ton âge
          </p>
          <ul className="text-sm text-white/75 space-y-1">
            {ageNotes.map((n, i) => (
              <li key={i} data-testid={`fm-age-note-${i}`}>• {n.label}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Obligations de base */}
      <Block title="Étapes à compléter" color="#ef4444" list={fm.base_checklist} prefix="" />
      {/* Équipement par discipline */}
      <Block title="Équipement obligatoire" color="#D90429" list={def.obligatoire} prefix="eq_" />
      <Block title="Recommandé" color="#c9a961" list={def.recommande} prefix="eq_" />
      <Block title="Avancé / compétition" color="#a855f7" list={def.avance} prefix="eq_" />

      {fm.verified_final && (
        <div className="mt-3 border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 flex items-center gap-2 text-sm text-emerald-200" data-testid="fm-verified-final">
          <CheckCircle size={18} weight="fill" /> Ton dossier premier mois a été validé par le coach. Bienvenue au dojo ! 🥋
        </div>
      )}
    </section>
  );
}


/* ── Parrainage Kaizen en ligne ── */
function MemberReferralCard({ email }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!email) return;
    memberApi.get(`/kaizen-online/me/${encodeURIComponent(email)}`)
      .then((r) => setInfo(r.data)).catch(() => setInfo(false));
  }, [email]);

  if (info === null || info === false) return null;

  const shareUrl = info.referral_code
    ? `${window.location.origin}/kaizen-en-ligne?ref=${info.referral_code}`
    : `${window.location.origin}/kaizen-en-ligne`;

  const copy = (txt, label) => {
    navigator.clipboard.writeText(txt);
    toast.success(`${label} copié !`);
  };

  if (!info.is_subscriber) {
    return (
      <section className="border border-[#c9a961]/30 bg-gradient-to-r from-[#c9a961]/10 to-transparent p-6 mb-8 kz-fade-up" data-testid="member-kaizen-online-cta">
        <div className="flex items-start gap-4 flex-wrap">
          <BookOpen size={26} className="text-[#c9a961] shrink-0 mt-1" />
          <div className="flex-1 min-w-[220px]">
            <h3 className="kz-display text-xl">Kaizen en ligne — curriculum à distance</h3>
            <p className="text-white/60 text-sm mt-1">Révise tes techniques entre les cours, suis ta progression et débloque le curriculum complet. 49,99 $ / mois.</p>
          </div>
          <Link to="/kaizen-en-ligne" data-testid="member-discover-kaizen-online"
            className="bg-[#D90429] text-white px-5 py-2.5 text-sm font-bold flex items-center gap-2 hover:bg-white hover:text-[#D90429] transition-colors">
            Découvrir <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="border border-[#c9a961]/40 bg-white/[0.02] p-6 mb-8 kz-fade-up" data-testid="member-referral-card">
      <div className="flex items-center gap-2 mb-4">
        <Gift size={18} className="text-[#c9a961]" />
        <h3 className="kz-display text-lg">Parrainage Kaizen</h3>
      </div>
      <p className="text-white/60 text-sm mb-5">Partage ton code : ton filleul reçoit <b className="text-white">10 $</b> de rabais, et tu reçois <b className="text-white">10 $</b> de crédit après son 1<sup>er</sup> paiement.</p>
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="border border-white/10 p-4">
          <p className="kz-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Ton code</p>
          <div className="flex items-center justify-between gap-2">
            <span className="kz-display text-2xl text-[#c9a961]" data-testid="referral-code">{info.referral_code}</span>
            <button onClick={() => copy(info.referral_code, "Code")} data-testid="copy-referral-code" className="text-white/50 hover:text-white"><Copy size={18} /></button>
          </div>
        </div>
        <div className="border border-white/10 p-4">
          <p className="kz-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Filleuls actifs</p>
          <span className="kz-display text-2xl">{info.referrals_count}</span>
        </div>
        <div className="border border-white/10 p-4">
          <p className="kz-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Crédit gagné</p>
          <span className="kz-display text-2xl text-[#D90429]">{Number(info.credit_earned || 0).toFixed(0)} $</span>
        </div>
      </div>
      <button onClick={() => copy(shareUrl, "Lien de parrainage")} data-testid="copy-referral-link"
        className="mt-4 inline-flex items-center gap-2 border border-white/15 hover:border-[#c9a961] px-4 py-2.5 text-sm transition-colors">
        <ShareNetwork size={16} /> Copier mon lien de partage
      </button>
    </section>
  );
}



/* 🎓 Vue ÉLÈVE du Programme Amikobi (bulletin dans le portail membre) */
function AmikobiMemberCard() {
  const [rec, setRec] = useState(null);
  useEffect(() => {
    memberApi.get("/member/amikobi").then((r) => setRec(r.data)).catch(() => setRec({ linked: false }));
  }, []);
  if (!rec || !rec.linked) return null;
  const cnt = rec.attendance_counts || {};
  return (
    <div className="mb-8 border border-white/10 bg-black/40 p-6 kz-fade-up" data-testid="amikobi-member-card">
      <div className="flex items-center gap-3 mb-4">
        <GraduationCap size={22} weight="duotone" className="text-[#D90429]" />
        <div>
          <p className="kz-mono text-[10px] tracking-[0.26em] uppercase text-white/40">Programme Amikobi</p>
          <h2 className="kz-display text-2xl font-medium">Mon bulletin de progrès</h2>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="border border-white/10 p-3 text-center"><div className="text-2xl font-bold text-[#D90429]">{rec.attendance_rate != null ? `${rec.attendance_rate}%` : "—"}</div><div className="text-xs text-white/40">Présence</div></div>
        <div className="border border-white/10 p-3 text-center"><div className="text-2xl font-bold">{rec.attendance_total}</div><div className="text-xs text-white/40">Cours suivis</div></div>
        <div className="border border-white/10 p-3 text-center"><div className="text-2xl font-bold">{(rec.notes || []).length}</div><div className="text-xs text-white/40">Notes</div></div>
      </div>
      {(rec.notes || []).length > 0 && (
        <div className="flex flex-col gap-2">
          {rec.notes.slice(0, 5).map((n) => (
            <div key={n.id} className="border border-white/10 p-3">
              <div className="flex items-center justify-between">
                <span className="kz-mono text-[10px] tracking-[0.2em] uppercase text-[#D90429]">{n.category}</span>
                <span className="text-xs text-white/40">{n.date}{n.rating ? ` · ${n.rating}/5` : ""}</span>
              </div>
              <p className="kz-body text-sm text-white/80 mt-1">{n.note}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



/* ─────────────────────────── TABLEAU DE BORD ────────────────────────────── */
function MemberDashboard({ profile, setProfile, onLogout }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [schedFor, setSchedFor] = useState(null);      // nom de discipline sélectionnée
  const [schedData, setSchedData] = useState(null);
  const [schedLoading, setSchedLoading] = useState(false);
  const [plans, setPlans] = useState(null);            // {plans, disciplines}
  const [planDiscipline, setPlanDiscipline] = useState("");
  const [planBusy, setPlanBusy] = useState(null);

  const openSchedule = async (nom) => {
    if (schedFor === nom) { setSchedFor(null); return; }
    setSchedFor(nom); setSchedLoading(true); setSchedData(null);
    try {
      const r = await memberApi.get(`/member/schedule?discipline=${encodeURIComponent(nom)}`);
      setSchedData(r.data);
    } catch { toast.error("Erreur de chargement de l'horaire"); }
    finally { setSchedLoading(false); }
  };

  useEffect(() => {
    memberApi.get("/member/subscription-plans")
      .then((r) => { setPlans(r.data); setPlanDiscipline(r.data.disciplines?.[0]?.nom || ""); })
      .catch(() => { /* non bloquant */ });
  }, []);

  const chooseplan = async (planKey) => {
    setPlanBusy(planKey);
    try {
      const r = await memberApi.post("/member/subscription-interest", { plan_key: planKey, discipline: planDiscipline });
      toast.success(r.data.message || "Demande envoyée !");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setPlanBusy(null); }
  };

  useEffect(() => {
    memberApi.get("/member/portal").then((r) => setData(r.data)).catch(() => toast.error("Erreur de chargement du portail"));
  }, []);

  if (!data) {
    return (
      <div className="kz-body min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <CircleNotch size={36} className="text-[#D90429] animate-spin" />
      </div>
    );
  }

  const firstName = data.member.first_name || "";
  const annonces = data.annonces || [];

  return (
    <div className="kz-body min-h-screen bg-[#0A0A0A] text-white p-5 md:p-12" data-testid="membre-portail">
      <InstallAppBanner who="membre" />
      {/* 📣 RUBAN D'ANNONCES — en haut, pleine largeur, bien visible (bienvenue · ambassadeur du mois · boutique officielle) */}
      {annonces.length > 0 && (
        <div className="-mx-5 md:-mx-12 -mt-5 md:-mt-12 mb-8 border-b-2 border-[#C9A961]/70 bg-gradient-to-r from-[#C9A961]/[0.16] via-[#0A0A0A] to-[#D90429]/[0.14] py-4 md:py-5 overflow-hidden" data-testid="module-annonces">
          <div className="kz-marquee-track">
            {[...annonces, ...annonces].map((a, i) => (
              <span key={i} className="kz-body text-sm md:text-base tracking-wide text-white/80 mx-10 inline-flex items-center gap-3">
                <Megaphone size={18} weight="fill" className="text-[#D90429]" />
                <b className="text-white text-base md:text-lg">{a.titre}</b> <span className="text-white/60">—</span> {a.corps}
              </span>
            ))}
          </div>
        </div>
      )}
      {/* En-tête */}
      <header className="flex items-start justify-between gap-4 flex-wrap mb-10">
        <div className="kz-fade-up flex items-center gap-4">
          {profile.picture && (
            <img src={profile.picture} alt="" referrerPolicy="no-referrer" data-testid="member-avatar"
              className="w-14 h-14 rounded-full object-cover border border-white/15 shrink-0" />
          )}
          <div>
          <p className="kz-mono text-[10px] tracking-[0.26em] uppercase text-white/40">
            KAIZEN SYSTEM · Espace Membres
          </p>
          <h1 className="kz-display text-3xl md:text-5xl font-medium mt-2" data-testid="member-greeting">
            {firstName ? <>Bienvenue, <span className="text-[#D90429]">{firstName}</span>.</> : "Bienvenue."}
          </h1>
          <p className="kz-body text-white/50 mt-1 text-sm">{data.dojo.name} · {data.dojo.ville}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing(true)} data-testid="edit-profile-btn"
            className="border border-white/15 hover:border-[#D90429] px-4 py-2.5 flex items-center gap-2 text-sm transition-colors">
            <PencilSimple size={16} /> Mon profil
          </button>
          <button onClick={onLogout} data-testid="member-logout-btn"
            className="border border-white/15 hover:border-white/40 px-4 py-2.5 flex items-center gap-2 text-sm transition-colors">
            <SignOut size={16} /> Quitter
          </button>
        </div>
      </header>

      {/* Bannière prioritaire (annonce importante — ex. fermeture école) */}
      {data.banniere && (
        <div className="mb-6 border border-[#D90429]/50 bg-[#D90429]/10 p-5 flex items-start gap-4" data-testid="annonce-banniere">
          <Megaphone size={22} weight="fill" className="text-[#D90429] shrink-0 mt-0.5" />
          <div>
            <p className="kz-display text-lg font-medium text-white" data-testid="annonce-banniere-titre">{data.banniere.titre}</p>
            <p className="kz-body text-sm text-white/70 mt-1" data-testid="annonce-banniere-corps">{data.banniere.corps}</p>
          </div>
        </div>
      )}

      {/* 🧭 Navigation rapide — ordre de lecture d'un nouveau membre */}
      <nav className="sticky top-0 z-30 -mx-5 md:-mx-12 px-5 md:px-12 py-2 mb-6 bg-[#0A0A0A]/90 backdrop-blur border-b border-white/10 flex gap-2 overflow-x-auto items-center" data-testid="member-quick-nav">
        <MemberHelpButton memberName={data.member.full_name} />
        {[
          ["member-dossier", "1 · Mon dossier"], ["member-first-month", "2 · Mon premier mois"], ["member-equipment-hub", "3 · Équipement & boutique"],
          ["member-checkin", "4 · Check-in & carte"], ["member-payment-card", "5 · Paiements"], ["member-academy-hub", "6 · Académie & progression"], ["module-horaire", "7 · Horaire & coachs"], ["module-carnet", "8 · Carnet de progression"],
        ].map(([id, label]) => (
          <button key={id} onClick={() => { const h = { "member-checkin": "checkin", "member-academy-hub": "carnet", "member-payment-card": "paiements", "member-dossier": "dossier", "member-equipment-hub": "equipement", "module-horaire": "horaire", "module-carnet": "carnet-progression" }[id]; if (h) window.history.replaceState({}, "", `/membres/${h}`); window.dispatchEvent(new PopStateEvent("popstate")); document.querySelector(`[data-testid="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }); }} data-testid={`quick-nav-${id}`}
            className="shrink-0 kz-mono text-[10px] tracking-[0.18em] uppercase border border-white/15 hover:border-[#C9A961] hover:text-[#C9A961] text-white/70 px-3 py-1.5 transition-colors">{label}</button>
        ))}
      </nav>

      {/* ✅ Mes prochaines étapes — checklist automatique (profil · tailles · carte · paiement) */}
      <MemberOnboardingChecklist onEditProfile={() => setEditing(true)} />

      {/* 💛 Carte de bienvenue — Ambassadeur KAIZEN */}
      {data.welcome && (
        <div className="mb-8 border p-5 flex items-start gap-5 flex-wrap kz-fade-up"
          style={{ borderColor: `${data.welcome.accent}66`, background: `${data.welcome.accent}12` }}
          data-testid="welcome-ambassador-card">
          <img src={data.welcome.photo} alt={data.welcome.ambassador_name}
            className="w-20 h-20 rounded-full object-cover shrink-0"
            style={{ border: `3px solid ${data.welcome.accent}` }} />
          <div className="flex-1 min-w-[220px]">
            <div className="kz-mono text-[10px] tracking-[0.26em] uppercase" style={{ color: data.welcome.accent }}>
              Ambassadeur KAIZEN · {data.welcome.ambassador_name}
            </div>
            <p className="kz-display text-lg text-white mt-1">{data.welcome.ambassador_tagline}</p>
            <p className="kz-body text-sm text-white/75 mt-2 leading-relaxed">{data.welcome.message}</p>
          </div>
        </div>
      )}

      {/* ── 1 · MON DOSSIER ── */}
      <MemberDossier />

      {/* ── 2 · MON PREMIER MOIS (juste sous Mon dossier) ── */}
      <MemberFirstMonth />

      {/* ── 3 · ÉQUIPEMENT & BOUTIQUE — Mon équipement · Mes tailles et mon uniforme · Boutique (prix membres) regroupés ── */}
      <MemberDrops onGoToProduct={(pid, sku) => { const el = document.querySelector(`[data-testid="boutique-item-${sku || pid}"]`) || document.querySelector('[data-testid="member-equipment-hub"]'); el?.scrollIntoView({ behavior: "smooth", block: "center" }); el?.classList.add("ring-2", "ring-[#D90429]"); setTimeout(() => el?.classList.remove("ring-2", "ring-[#D90429]"), 3000); }} />
      <section className="border border-[#C9A961]/40 bg-[#C9A961]/[0.04] p-6 mb-8 kz-fade-up" data-testid="member-equipment-hub">
        <p className="kz-mono text-[10px] tracking-[0.26em] uppercase text-[#C9A961]">Équipement & boutique</p>
        <h2 className="kz-display text-2xl mt-1">Mon équipement, mes tailles et la boutique officielle</h2>
        <p className="text-white/50 text-sm mt-1">Ce qu'il te faut pour ton programme → tes tailles et ton uniforme → commande au prix membre. Tout au même endroit.</p>
        <MyEquipment />
        <MySizesAndPreorders />
        <div className="mt-6"><MemberBoutique /></div>
      </section>

      {/* ── 4 · CHECK-IN, PROFIL & CARTE QR ── */}
      <MemberCheckin />
      <MemberCompleteProfile />
      <MemberCardSection />
      <MemberSamouraisSection />
      {/* ── 5 · MES PAIEMENTS — frais · carte · échéancier · famille · PKH regroupés ── */}
      <MemberPaymentsHub>
        <MemberPaymentSituation />
        <MemberFeeButton />
        <MemberPaymentCard />
        <MemberBillingSchedule />
        <MemberFamilySection />
        <MemberPkhAddon />
      </MemberPaymentsHub>

      {/* ── 6 · ACADÉMIE, PROGRAMMES & PROGRESSION ── */}
      <div data-testid="member-academy-hub">
        <AcademyMemberSection email={data.member.email} />
        <AmikobiMemberCard />
        <MemberIntegrationProgress ModuleTitle={ModuleTitle} />
        <MemberAttendanceBadge />
      </div>

      {/* ── 7 · COMMUNAUTÉ, PARRAINAGE, ASSURANCE ── */}
      <MemberReferralCard email={data.member.email} />
      <MemberCommunauteKien />
      <MemberInsuranceSection />

      {/* Grille Bento */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-min">
        {/* Horaire & disciplines */}
        <section className="md:col-span-8 border border-white/10 p-7 kz-fade-up" data-testid="module-horaire">
          <ModuleTitle icon={Barbell} over="Entraînement">Horaire & disciplines</ModuleTitle>
          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            {data.disciplines.map((d) => (
              <button key={d.key} onClick={() => openSchedule(d.nom)}
                className={`flex items-center gap-4 border p-4 transition-colors text-left w-full ${schedFor === d.nom ? "border-[#D90429]/60 bg-[#D90429]/[0.06]" : "border-white/5 hover:border-white/15 bg-white/[0.02]"}`}
                data-testid={`discipline-${d.key}`}>
                <img src={d.logo} alt="" className="w-12 h-12 object-contain shrink-0" />
                <div>
                  <div className="kz-display text-lg leading-tight flex items-center gap-2">{d.nom}
                    <span className="text-[10px] text-[#D90429] kz-mono">{schedFor === d.nom ? "▲ HORAIRE" : "▼ VOIR HORAIRE"}</span>
                  </div>
                  <div className="text-white/45 text-xs mt-0.5">{d.desc}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Panneau horaire de la discipline sélectionnée */}
          {schedFor && (
            <div className="mt-5 border border-white/10 bg-black/30 p-5 kz-fade-up" data-testid="discipline-schedule">
              <div className="kz-mono text-[11px] tracking-[0.2em] uppercase text-white/50 mb-3">Horaire — {schedFor}</div>
              {schedLoading ? (
                <div className="text-white/40 text-sm flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Chargement…</div>
              ) : !schedData || schedData.days.length === 0 ? (
                <p className="text-white/40 text-sm">Aucun cours public programmé pour cette discipline. Contacte le dojo pour les détails.</p>
              ) : (
                <div className="space-y-4">
                  {schedData.days.map((day) => (
                    <div key={day.jour}>
                      <div className="kz-display text-sm text-[#D90429] mb-1.5">{day.jour}</div>
                      <div className="space-y-1.5">
                        {day.cours.map((c, i) => (
                          <div key={i} className="flex items-baseline gap-3 text-sm border-b border-white/5 pb-1.5" data-testid="schedule-course">
                            <span className="kz-mono text-white/80 shrink-0 w-24">{c.heure_debut}–{c.heure_fin}</span>
                            <span className="text-white/90 flex-1">{c.titre}
                              {(c.age_min != null || c.age_max != null) && <span className="text-white/40 text-xs ml-2">({c.age_min ?? "?"}-{c.age_max ?? "?"} ans)</span>}
                            </span>
                            <span className="text-white/40 text-xs hidden sm:block">{c.instructeur}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Abonnements — 3 offres + recommandé */}
        {plans && (
          <section className="md:col-span-12 border border-white/10 p-7 kz-fade-up" data-testid="module-abonnements">
            <ModuleTitle icon={Barbell} over="Adhésion">Choisis ton abonnement</ModuleTitle>
            <div className="flex items-center gap-3 mt-4 flex-wrap">
              <span className="text-white/50 text-sm">Discipline :</span>
              <div className="flex gap-2 flex-wrap">
                {plans.disciplines.map((d) => (
                  <button key={d.key} onClick={() => setPlanDiscipline(d.nom)}
                    className={`px-3 py-1.5 text-sm border transition-colors ${planDiscipline === d.nom ? "border-[#D90429] text-white bg-[#D90429]/10" : "border-white/10 text-white/60 hover:border-white/25"}`}
                    data-testid={`plan-discipline-${d.key}`}>{d.nom}</button>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mt-6">
              {plans.plans.map((p) => (
                <div key={p.key} data-testid={`plan-${p.key}`}
                  className={`relative border p-6 flex flex-col ${p.recommended ? "border-[#D90429] bg-[#D90429]/[0.05]" : "border-white/10 bg-white/[0.02]"}`}>
                  {p.recommended && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D90429] text-white text-[10px] kz-mono tracking-[0.2em] px-3 py-1 uppercase" data-testid="plan-recommended-badge">Recommandé</span>
                  )}
                  <div className="kz-display text-xl">{p.nom}</div>
                  <div className="text-white/45 text-xs mt-1">{p.sessions}</div>
                  <div className="mt-4 mb-1"><span className="kz-display text-4xl">{p.price} $</span><span className="text-white/40 text-sm"> / {p.cycle}</span>{p.taxes_en_sus && <span className="text-white/40 text-xs"> + taxes</span>}</div>
                  <ul className="mt-4 space-y-2 flex-1">
                    {p.features.map((f, i) => (
                      <li key={i} className="text-white/70 text-sm flex items-start gap-2"><span className="text-[#D90429] mt-0.5">✓</span>{f}</li>
                    ))}
                  </ul>
                  <button onClick={() => chooseplan(p.key)} disabled={planBusy === p.key}
                    className={`mt-6 py-3 text-sm kz-mono tracking-[0.15em] uppercase transition-colors disabled:opacity-50 ${p.recommended ? "bg-[#D90429] text-white hover:bg-[#b8031f]" : "border border-white/20 text-white hover:bg-white/5"}`}
                    data-testid={`plan-choose-${p.key}`}>
                    {planBusy === p.key ? "Envoi…" : "Choisir cette offre"}
                  </button>
                </div>
              ))}
            </div>
            <MemberProgramsSection programs={plans.programs} />
            <p className="kz-mono text-[10px] tracking-[0.18em] uppercase text-white/30 mt-5">
              Le dojo te contacte pour finaliser · sans engagement à long terme
            </p>
          </section>
        )}

        {/* Progression ceintures */}
        <section className="md:col-span-4 border border-white/10 bg-white/[0.03] p-7 kz-fade-up" data-testid="module-progression">
          <ModuleTitle icon={Medal} over="Ta progression">Ceintures</ModuleTitle>
          <BeltLadder progression={data.progression} />
        </section>

        {/* Coachs & équipe */}
        <section className="md:col-span-4 border border-white/10 p-7 kz-fade-up" data-testid="module-coachs">
          <ModuleTitle icon={UsersThree} over="L'équipe">Mes coachs</ModuleTitle>
          <div className="space-y-3 mt-5">
            {data.coaches.length === 0 && <p className="text-white/40 text-sm">Équipe bientôt en ligne.</p>}
            {data.coaches.slice(0, 5).map((c) => (
              <div key={c.id} className="flex items-center gap-3" data-testid={`coach-${c.id}`}>
                {c.photo ? <img src={c.photo} alt="" className="w-10 h-10 object-cover grayscale" />
                  : <div className="w-10 h-10 bg-white/5 flex items-center justify-center kz-display text-[#D90429]">{(c.full_name || "?")[0]}</div>}
                <div>
                  <div className="text-sm font-semibold">{c.full_name}</div>
                  <div className="kz-mono text-[10px] tracking-[0.12em] uppercase text-white/40">{c.role || "Coach"}</div>
                </div>
              </div>
            ))}
          </div>
          <Link to="/equipe" className="inline-flex items-center gap-1.5 kz-mono text-[10px] tracking-[0.2em] uppercase text-white/50 hover:text-[#D90429] mt-5 transition-colors" data-testid="view-team-link">
            Voir toute l'équipe <ArrowUpRight size={13} />
          </Link>
        </section>

        {/* Boutique & promos */}
        <Link to="/boutique-accueil" data-testid="module-boutique-link"
          className="md:col-span-4 border border-[#D90429]/30 bg-[#D90429]/10 p-7 flex flex-col justify-between group hover:bg-[#D90429]/20 transition-colors kz-fade-up">
          <ModuleTitle icon={Storefront} over="Membres" red>Boutique & promos</ModuleTitle>
          <div className="flex items-center justify-between mt-6">
            <p className="kz-body text-sm text-white/70">Équipements officiels & rabais membres.</p>
            <ArrowUpRight size={26} className="text-[#D90429] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </div>
        </Link>

        {/* Billetterie / Brackets */}
        <section className="md:col-span-4 border border-white/10 p-7 kz-fade-up" data-testid="module-events">
          <ModuleTitle icon={Ticket} over="À l'agenda">Calendrier — Dates importantes</ModuleTitle>
          <div className="space-y-3 mt-5">
            {data.events.length === 0 && data.competitions.length === 0 && (data.billetterie || []).length === 0 && (
              <p className="text-white/40 text-sm">Aucun événement à venir pour l'instant.</p>
            )}
            {(data.billetterie || []).map((b) => (
              <Link key={b.id} to={b.slug ? `/billetterie/${b.slug}` : "#"} className="flex items-center gap-3 border-l-2 border-[#D90429] pl-3 group" data-testid={`billet-${b.id}`}>
                <Ticket size={16} weight="fill" className="text-[#D90429] shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold group-hover:text-[#D90429] transition-colors">{b.title}</div>
                  <div className="kz-mono text-[10px] text-white/40">{b.date}{b.venue ? ` · ${b.venue}` : ""}</div>
                </div>
                <span className="kz-mono text-[9px] tracking-[0.18em] uppercase text-[#D90429] border border-[#D90429]/40 px-2 py-1 group-hover:bg-[#D90429] group-hover:text-white transition-colors">Réserver</span>
              </Link>
            ))}
            {data.events.map((ev) => (
              <div key={ev.id} className="flex items-center gap-3 border-l-2 border-white/15 pl-3" data-testid={`event-${ev.id}`}>
                <CalendarBlank size={16} className="text-white/40 shrink-0" />
                <div>
                  <div className="text-sm font-semibold">{ev.title}</div>
                  <div className="kz-mono text-[10px] text-white/40">{ev.date}{ev.location ? ` · ${ev.location}` : ""}</div>
                </div>
              </div>
            ))}
            {data.competitions.map((c) => (
              <Link key={c.id} to={c.slug ? `/competitions/${c.slug}` : "#"} className="flex items-center gap-3 border-l-2 border-white/20 hover:border-[#D90429] pl-3 transition-colors" data-testid={`comp-${c.id}`}>
                <Medal size={16} className="text-white/40 shrink-0" />
                <div>
                  <div className="text-sm font-semibold">{c.title}</div>
                  <div className="kz-mono text-[10px] text-white/40">{c.date} · Bracket / inscription</div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Tarifs */}
        <section className="md:col-span-12 border border-white/10 p-7 kz-fade-up" data-testid="module-tarifs">
          <ModuleTitle icon={Medal} over="Tarification">Tarifs & abonnements</ModuleTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
            {data.tariffs.length === 0 && <p className="text-white/40 text-sm">Tarifs communiqués au dojo.</p>}
            {data.tariffs.slice(0, 9).map((t, i) => (
              <div key={i} className="border border-white/5 bg-white/[0.02] p-4 flex items-baseline justify-between gap-2" data-testid={`tarif-${i}`}>
                <span className="text-sm text-white/80">{t.label}</span>
                <span className="kz-display text-lg text-[#D90429] whitespace-nowrap">
                  {t.price != null ? `${Number(t.price).toLocaleString("fr-CA")} $` : "—"}
                  <span className="text-white/30 text-xs kz-body"> /{t.billing_cycle || "mois"}</span>
                </span>
              </div>
            ))}
          </div>
          {data.tariffs.length > 9 && (
            <p className="kz-mono text-[10px] tracking-[0.18em] uppercase text-white/30 mt-4">+ {data.tariffs.length - 9} autres programmes disponibles au dojo</p>
          )}
        </section>

        {/* Mon carnet de progression */}
        <div className="md:col-span-12" data-testid="module-carnet">
          <CarnetModule />
        </div>

        {/* Parrainage */}
        <div className="md:col-span-12">
          <ReferralModule />
        </div>
      </div>

      <footer className="mt-12 text-center kz-mono text-[10px] tracking-[0.2em] uppercase text-white/25">
        KAIZEN SYSTEM · by Institut Impact Élite Martial · Espace réservé aux membres
      </footer>

      {editing && <ProfileModal profile={profile} progression={data.progression} disciplines={data.disciplines}
        onClose={() => setEditing(false)} onSaved={(m) => { setProfile(m); setData((d) => ({ ...d, member: m, progression: { ...d.progression, current: m.belt } })); setEditing(false); }} />}
    </div>
  );
}

function ModuleTitle({ icon: Icon, over, red, children }) {
  return (
    <div>
      <p className={`kz-mono text-[10px] tracking-[0.24em] uppercase ${red ? "text-[#D90429]/70" : "text-white/40"}`}>{over}</p>
      <h2 className={`kz-display text-2xl font-medium mt-1 flex items-center gap-2 ${red ? "text-[#D90429]" : "text-white"}`}>
        <Icon size={22} weight="light" /> {children}
      </h2>
    </div>
  );
}

function BeltLadder({ progression }) {
  const BELT_HEX = {
    Blanche: "#f5f5f5", Jaune: "#facc15", Orange: "#fb923c", Verte: "#22c55e",
    Bleue: "#3b82f6", Violette: "#a855f7", Brune: "#92400e", Noire: "#111111",
  };
  return (
    <div className="mt-5 relative pl-2">
      <div className="absolute left-[11px] top-2 bottom-2 w-px bg-white/10" />
      <div className="space-y-3">
        {progression.ladder.map((b, i) => {
          const active = i === progression.current_index;
          const done = i < progression.current_index;
          return (
            <div key={b} className="flex items-center gap-4 relative" data-testid={`belt-${b}`}>
              <span className="w-[18px] h-[18px] rounded-full border-2 shrink-0 z-10"
                style={{ background: done || active ? BELT_HEX[b] : "transparent", borderColor: active ? "#D90429" : "rgba(255,255,255,0.2)", boxShadow: active ? "0 0 0 4px rgba(217,4,41,0.25)" : "none" }} />
              <span className={`text-sm ${active ? "text-white font-bold" : done ? "text-white/60" : "text-white/35"}`}>
                {b}{active && <span className="kz-mono text-[10px] tracking-[0.2em] uppercase text-[#D90429] ml-2">· actuelle</span>}
              </span>
            </div>
          );
        })}
      </div>
      {progression.next && (
        <p className="kz-body text-xs text-white/40 mt-4">Prochaine étape : <b className="text-white/70">{progression.next}</b></p>
      )}
    </div>
  );
}

function ProfileModal({ profile, progression, disciplines, onClose, onSaved }) {
  const [fullName, setFullName] = useState(profile.full_name || "");
  const [belt, setBelt] = useState(profile.belt || "Blanche");
  const [discipline, setDiscipline] = useState(profile.discipline || "");
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const r = await memberApi.patch("/member/me", { full_name: fullName, belt, discipline });
      toast.success("Profil mis à jour");
      onSaved(r.data.member);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBusy(false);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="profile-modal">
      <div className="bg-[#0A0A0A] border border-white/15 w-full max-w-md p-8" onClick={(e) => e.stopPropagation()}>
        <h3 className="kz-display text-2xl mb-1">Mon profil</h3>
        <p className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mb-6">Personnalise ton espace</p>
        <div className="space-y-5">
          <div>
            <label className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-2">Prénom & nom</label>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="profile-name-input"
              className="w-full bg-transparent border-b border-white/20 focus:border-[#D90429] outline-none text-white py-2" />
          </div>
          <div>
            <label className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-2">Ma ceinture</label>
            <select value={belt} onChange={(e) => setBelt(e.target.value)} data-testid="profile-belt-select"
              className="w-full bg-[#0A0A0A] border border-white/20 focus:border-[#D90429] outline-none text-white py-2 px-2">
              {progression.ladder.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-2">Discipline principale</label>
            <select value={discipline} onChange={(e) => setDiscipline(e.target.value)} data-testid="profile-discipline-select"
              className="w-full bg-[#0A0A0A] border border-white/20 focus:border-[#D90429] outline-none text-white py-2 px-2">
              <option value="">—</option>
              {disciplines.map((d) => <option key={d.key} value={d.nom}>{d.nom}</option>)}
            </select>
          </div>
        </div>
        <button onClick={save} disabled={busy} data-testid="profile-save-btn"
          className="w-full bg-[#D90429] text-white py-3.5 font-bold tracking-wider hover:bg-white hover:text-black transition-colors mt-7 flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <CircleNotch size={18} className="animate-spin" /> : <><CheckCircle size={18} weight="fill" /> Enregistrer</>}
        </button>
      </div>
    </div>
  );
}


/* ─────────────────────────── MODULE CARNET ──────────────────────────────── */
const DISCIPLINE_LABELS = {
  bjj: "Jiu-jitsu brésilien",
  jujutsu_japonais: "Jiu-jitsu Can Ryu",
  muay_thai_kickboxing: "Kickboxing / Muay Thaï",
  muay_thai: "Kickboxing / Muay Thaï",
  kickboxing: "Kickboxing / Muay Thaï",
  power_kicker: "Kickboxing / Muay Thaï",
};

function CarnetModule() {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(null);

  const downloadPdf = async (c) => {
    setPdfBusy(c.profile_id);
    try {
      const r = await memberApi.get(`/member/carnet/${c.profile_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `carnet-${(c.student_name || "eleve").replace(/\s+/g, "_")}-${c.discipline || ""}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Carnet PDF téléchargé");
    } catch { toast.error("Téléchargement impossible — réessaie dans un instant"); }
    setPdfBusy(null);
  };

  const load = () =>
    memberApi.get("/member/carnet").then((r) => setData(r.data)).catch(() => setData({ linked: false, carnets: [] }));

  useEffect(() => { load(); }, []);

  async function claim(e) {
    e?.preventDefault();
    if (!code.trim()) return toast.error("Entre le code fourni par ton coach");
    setBusy(true);
    try {
      await memberApi.post("/member/carnet/claim", { code: code.trim() });
      toast.success("Carnet lié à ton compte ! 🥋");
      setCode("");
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Code invalide");
    }
    setBusy(false);
  }

  if (!data) {
    return (
      <section className="border border-white/10 p-7" data-testid="module-carnet-loading">
        <ModuleTitle icon={BookOpen} over="Ta progression">Mon carnet</ModuleTitle>
        <p className="text-white/40 text-sm mt-4">Chargement de ton carnet…</p>
      </section>
    );
  }

  const carnets = data.carnets || [];

  return (
    <section className="border border-white/10 p-7" data-testid="module-carnet-content">
      <ModuleTitle icon={BookOpen} over="Ta progression technique">Mon carnet</ModuleTitle>

      {carnets.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5" data-testid="carnet-list">
          {carnets.map((c) => (
            <a
              key={c.profile_id}
              href={c.public_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`carnet-${c.profile_id}`}
              className="border border-white/10 hover:border-[#D90429] bg-white/[0.02] p-5 group transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="kz-display text-lg leading-tight">{c.student_name}</div>
                  <div className="kz-mono text-[10px] tracking-[0.12em] uppercase text-white/40 mt-1">
                    {DISCIPLINE_LABELS[c.discipline] || c.discipline || "—"}
                  </div>
                </div>
                <ArrowUpRight size={20} className="text-[#D90429] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform shrink-0" />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="kz-body text-xs text-white/60">
                  {c.current_belt || "—"} <span className="text-white/30">→</span> {c.target_belt || "—"}
                </span>
                {c.global_average != null && (
                  <span className="kz-display text-xl text-[#D90429]">{c.global_average}%</span>
                )}
              </div>
              {c.global_average != null && (
                <div className="mt-2 h-1.5 w-full bg-white/10 rounded-full overflow-hidden" data-testid={`carnet-progress-${c.profile_id}`}>
                  <div className="h-full bg-gradient-to-r from-[#D90429] to-[#f5a623] transition-all"
                    style={{ width: `${Math.min(100, Math.max(0, c.global_average))}%` }} />
                </div>
              )}
              <div className="kz-mono text-[10px] tracking-[0.12em] uppercase text-white/30 mt-3">
                {c.techniques_count} technique{c.techniques_count > 1 ? "s" : ""} suivie{c.techniques_count > 1 ? "s" : ""}
              </div>
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); downloadPdf(c); }} disabled={pdfBusy === c.profile_id}
                className="mt-3 w-full border border-white/15 hover:border-[#D90429] hover:text-white text-white/70 text-xs py-2 kz-mono tracking-[0.1em] uppercase disabled:opacity-50"
                data-testid={`carnet-pdf-${c.profile_id}`}>
                {pdfBusy === c.profile_id ? "Préparation du PDF…" : "⬇ Télécharger en PDF"}
              </button>
            </a>
          ))}
        </div>
      ) : (
        <p className="kz-body text-sm text-white/50 mt-5">
          Aucun carnet lié pour l'instant. Ton coach peut t'associer à ton carnet via ton courriel, ou te donner un code de liaison à entrer ci-dessous.
        </p>
      )}

      {/* Lier par code */}
      <form onSubmit={claim} className="mt-6 flex flex-col sm:flex-row gap-3 items-stretch" data-testid="carnet-claim-form">
        <div className="flex-1 flex items-center gap-3 border border-white/10 bg-black/40 px-4">
          <LinkSimple size={18} className="text-white/40" />
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={6}
            placeholder="Code de liaison (ex. 9F3A2C)"
            data-testid="carnet-code-input"
            className="w-full bg-transparent outline-none text-white placeholder-white/30 kz-mono tracking-[0.2em] py-3"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="carnet-claim-btn"
          className="bg-white text-black px-6 py-3 font-bold tracking-wider hover:bg-[#D90429] hover:text-white transition-colors flex items-center gap-2 justify-center disabled:opacity-50"
        >
          {busy ? <CircleNotch size={18} className="animate-spin" /> : <>Lier mon carnet <ArrowRight size={16} weight="bold" /></>}
        </button>
      </form>
    </section>
  );
}

/* ─────────────────────────── MODULE PARRAINAGE ──────────────────────────── */
function ReferralModule() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);

  useEffect(() => {
    memberApi.get("/member/referral").then((r) => setData(r.data)).catch(() => { /* silencieux */ });
    memberApi.get("/member/referral/leaderboard").then((r) => setBoard(r.data)).catch(() => { /* silencieux */ });
  }, []);

  if (!data) {
    return (
      <section className="border border-white/10 p-7" data-testid="module-parrainage">
        <ModuleTitle icon={Gift} over="Croissance" red>Parrainage</ModuleTitle>
        <p className="text-white/40 text-sm mt-4">Chargement de ton lien…</p>
      </section>
    );
  }

  const link = `${window.location.origin}${data.path}`;
  const { clicks, conversions, next_threshold, next_label } = data.stats;
  const progressPct = next_threshold ? Math.min(100, Math.round((conversions / next_threshold) * 100)) : 100;

  function copy() {
    navigator.clipboard.writeText(link);
    toast.success("Lien copié ! Partage-le à tes amis.");
  }
  async function share() {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Institut Impact Élite Martial",
          text: "Viens t'entraîner avec moi — cours d'essai à 19,99 $ !", url: link });
      } catch { /* annulé */ }
    } else { copy(); }
  }

  return (
    <section className="border border-[#D90429]/30 bg-[#D90429]/[0.06] p-7" data-testid="module-parrainage">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <ModuleTitle icon={Gift} over="Invite tes amis · gagne des récompenses" red>Ton lien de parrainage</ModuleTitle>
        <div className="flex gap-4">
          <Stat value={clicks} label="Clics" />
          <Stat value={conversions} label="Filleuls" red />
          <Stat value={data.rewards.length} label="Récompenses" />
        </div>
      </div>

      {/* Lien + actions */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 bg-black/40 border border-white/10 px-4 py-3 kz-mono text-sm text-white/70 truncate" data-testid="referral-link">{link}</div>
        <button onClick={copy} data-testid="referral-copy-btn"
          className="bg-white text-black px-5 py-3 font-bold tracking-wider hover:bg-[#D90429] hover:text-white transition-colors flex items-center gap-2 justify-center">
          <Copy size={16} weight="bold" /> Copier
        </button>
        <button onClick={share} data-testid="referral-share-btn"
          className="border border-white/15 hover:border-[#D90429] px-5 py-3 transition-colors flex items-center gap-2 justify-center">
          <ShareNetwork size={16} /> Partager
        </button>
      </div>

      {/* Progression vers prochaine récompense */}
      {next_threshold && (
        <div className="mt-6">
          <div className="flex items-center justify-between text-xs text-white/50 mb-2">
            <span className="kz-mono tracking-[0.15em] uppercase">Prochaine récompense · {next_label}</span>
            <span>{conversions}/{next_threshold} filleuls</span>
          </div>
          <div className="h-1.5 bg-white/10 overflow-hidden">
            <div className="h-full bg-[#D90429] transition-all duration-700" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}

      {/* Récompenses débloquées */}
      {data.rewards.length > 0 && (
        <div className="mt-6">
          <p className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mb-3">Récompenses débloquées</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="referral-rewards">
            {data.rewards.map((r) => (
              <div key={r.id} className="border border-[#D90429]/40 bg-black/30 p-4" data-testid={`reward-${r.threshold}`}>
                <div className="flex items-center gap-2 text-[#D90429]"><Gift size={16} weight="fill" /><span className="text-sm font-semibold text-white">{r.label}</span></div>
                <div className="mt-2 kz-mono text-lg tracking-[0.15em] text-[#D90429]">{r.code}</div>
                <div className="kz-mono text-[10px] tracking-[0.12em] uppercase text-white/30 mt-1">À présenter au dojo / en boutique</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.rewards.length === 0 && (
        <p className="kz-body text-sm text-white/50 mt-5">
          Partage ton lien : dès qu'un ami réserve son cours d'essai, tu débloques ta première récompense (rabais boutique 15 %). 🥋
        </p>
      )}

      {/* Classement Ambassadeurs du mois */}
      {board && board.leaderboard && board.leaderboard.length > 0 && (
        <div className="mt-8 pt-7 border-t border-white/10" data-testid="leaderboard">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-[#D90429]">
              <Trophy size={20} weight="fill" />
              <h3 className="kz-display text-xl text-white">Ambassadeurs du mois</h3>
            </div>
            <span className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/40">{board.month}</span>
          </div>

          {/* Bannière champion + récompense */}
          {board.champion && (
            <div className="mt-4 border border-[#D90429]/40 bg-[#D90429]/10 p-4 flex items-center gap-3 flex-wrap" data-testid="champion-banner">
              <Crown size={26} weight="fill" className="text-[#D90429] shrink-0" />
              <div className="flex-1 min-w-[180px]">
                <div className="kz-display text-lg leading-tight">
                  {board.champion.is_me ? "Tu es l'Ambassadeur du mois ! 🏆" : `${board.champion.name} mène la danse`}
                </div>
                <div className="kz-body text-xs text-white/60 mt-0.5">
                  Récompense du #1 : <b className="text-white">{board.champion.reward_label}</b>
                </div>
              </div>
              <span className="kz-display text-2xl text-[#D90429]">{board.champion.count}</span>
            </div>
          )}

          <div className="mt-4 space-y-2">
            {board.leaderboard.map((row) => (
              <div
                key={row.member_id}
                data-testid={`leaderboard-row-${row.rank}`}
                className={`flex items-center gap-4 px-4 py-3 border transition-colors ${
                  row.is_me ? "border-[#D90429] bg-[#D90429]/10" : "border-white/10 bg-white/[0.02]"
                }`}
              >
                <span className={`kz-display text-lg w-7 text-center ${row.rank === 1 ? "text-[#D90429]" : "text-white/50"}`}>
                  {row.rank === 1 ? <Crown size={20} weight="fill" className="inline text-[#D90429]" /> : row.rank}
                </span>
                <span className="flex-1 text-sm font-semibold flex items-center gap-2 flex-wrap">
                  {row.name}
                  {row.rank === 1 && (
                    <span className="kz-mono text-[9px] tracking-[0.16em] uppercase text-[#D90429] border border-[#D90429]/40 px-1.5 py-0.5">Ambassadeur du mois</span>
                  )}
                  {row.is_me && <span className="kz-mono text-[10px] tracking-[0.2em] uppercase text-[#D90429]">· toi</span>}
                </span>
                <span className="kz-display text-lg text-[#D90429]">{row.count}</span>
                <span className="kz-mono text-[9px] tracking-[0.18em] uppercase text-white/40">filleuls</span>
              </div>
            ))}
          </div>
          {board.my_rank == null && (
            <p className="kz-body text-xs text-white/40 mt-3">
              Tu n'es pas encore au classement ce mois-ci. Invite un ami pour y entrer et viser le titre d'Ambassadeur du mois ! 🥋
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function Stat({ value, label, red }) {
  return (
    <div className="text-center">
      <div className={`kz-display text-2xl ${red ? "text-[#D90429]" : "text-white"}`}>{value}</div>
      <div className="kz-mono text-[9px] tracking-[0.18em] uppercase text-white/40">{label}</div>
    </div>
  );
}

// 🤝 Communauté K'Ien — annuaire opt-in (Loi 25) : consentement explicite, champs publics limités, aucune messagerie.
function MemberCommunauteKien() {
  const DISC = { kickboxing_muay_thai: "Kickboxing / Muay thaï", jiu_jitsu_can_ryu: "Jiu-jitsu Can Ryu", bjj: "BJJ" };
  const LVL = { debutant: "Ceintures Jaune–Orange", intermediaire: "Ceintures Verte–Bleue", avance: "Ceintures Mauve–Noire" };
  const [tab, setTab] = useState("annuaire"); // annuaire | profil | demandes | groupes
  const [profile, setProfile] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({ consent: false, rules_accepted: false, display_name: "", region: "", province_pays: "", disciplines: [], level: "", objective: "", accepts_contact: true });
  const [dir, setDir] = useState([]);
  const [filters, setFilters] = useState({ region: "", province_pays: "", discipline: "", level: "" });
  const [busy, setBusy] = useState(false);
  const [reqs, setReqs] = useState({ incoming: [], outgoing: [] });
  const [groups, setGroups] = useState([]);
  const [openGroup, setOpenGroup] = useState(null);
  const [meetups, setMeetups] = useState([]);
  const [newMeetup, setNewMeetup] = useState({ title: "", venue_public: "", date: "", level: "", rules: "" });
  const [threads, setThreads] = useState([]);
  const [chat, setChat] = useState(null); // {other_member_id, display_name}
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState("");
  const [unread, setUnread] = useState(0);
  const [myMeetups, setMyMeetups] = useState([]);

  const loadUnread = useCallback(() => {
    memberApi.get("/member/community/unread").then((r) => setUnread(r.data.unread_messages || 0)).catch(() => {});
  }, []);
  const loadMyMeetups = useCallback(() => {
    memberApi.get("/member/community/my-meetups").then((r) => setMyMeetups(r.data.meetups || [])).catch(() => {});
  }, []);

  const loadThreads = useCallback(() => {
    memberApi.get("/member/community/threads").then((r) => setThreads(r.data.threads || [])).catch(() => {});
  }, []);
  const openChat = async (t) => {
    setChat({ other_member_id: t.other_member_id, display_name: t.display_name });
    try { const r = await memberApi.get(`/member/community/messages/${t.other_member_id}`); setMessages(r.data.messages || []); loadThreads(); }
    catch { setMessages([]); }
  };
  const sendMsg = async () => {
    if (!msgInput.trim() || !chat) return;
    try {
      const r = await memberApi.post(`/member/community/messages/${chat.other_member_id}`, { body: msgInput });
      setMessages((m) => [...m, r.data.message]); setMsgInput("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi."); }
  };

  const loadReqs = useCallback(() => {
    memberApi.get("/member/community/contact-requests").then((r) => setReqs({ incoming: r.data.incoming || [], outgoing: r.data.outgoing || [] })).catch(() => {});
  }, []);
  const loadGroups = useCallback(() => {
    memberApi.get("/member/community/groups").then((r) => setGroups(r.data.groups || [])).catch(() => {});
  }, []);
  const loadMeetups = useCallback((gid) => {
    memberApi.get(`/member/community/groups/${gid}/meetups`).then((r) => setMeetups(r.data.meetups || [])).catch(() => setMeetups([]));
  }, []);

  const sendContact = async (m) => {
    const msg = window.prompt(`Message pour ${m.display_name} (optionnel) :`, "Salut ! On s'entraîne ensemble ?");
    if (msg === null) return;
    try { await memberApi.post("/member/community/contact-request", { to_profile_id: m.id, message: msg }); toast.success("Demande de contact envoyée."); loadReqs(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi."); }
  };
  const reportMember = async (m) => {
    const reason = window.prompt(`Signaler ${m.display_name} — raison :`, "");
    if (reason === null) return;
    try { await memberApi.post("/member/community/report", { profile_id: m.id, reason }); toast.success("Profil signalé aux administrateurs."); }
    catch { toast.error("Échec du signalement."); }
  };
  const blockMember = async (m) => {
    if (!window.confirm(`Bloquer ${m.display_name} ? Vous ne verrez plus son profil et il ne pourra plus vous contacter.`)) return;
    try { await memberApi.post("/member/community/block", { profile_id: m.id }); toast.success("Membre bloqué."); loadDir(); }
    catch { toast.error("Échec du blocage."); }
  };
  const respondReq = async (rid, action) => {
    try { await memberApi.post(`/member/community/contact-request/${rid}/respond`, { action }); toast.success(action === "accept" ? "Demande acceptée !" : "Demande refusée."); loadReqs(); }
    catch { toast.error("Échec."); }
  };
  const toggleGroup = async (g) => {
    try { await memberApi.post(`/member/community/groups/${g.id}/${g.joined ? "leave" : "join"}`); loadGroups(); }
    catch { toast.error("Échec."); }
  };
  const createMeetup = async () => {
    if (!newMeetup.title || !newMeetup.venue_public || !newMeetup.date) { toast.error("Titre, lieu public et date requis."); return; }
    try { await memberApi.post(`/member/community/groups/${openGroup.id}/meetups`, newMeetup); toast.success("Rencontre créée !"); setNewMeetup({ title: "", venue_public: "", date: "", level: "", rules: "" }); loadMeetups(openGroup.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };
  const rsvp = async (mid, going) => {
    try { await memberApi.post(`/member/community/meetups/${mid}/rsvp`, { going }); loadMeetups(openGroup.id); }
    catch { toast.error("Échec."); }
  };

  const loadProfile = useCallback(() => {
    memberApi.get("/member/community/profile").then((r) => {
      setProfile(r.data.profile);
      if (r.data.profile) {
        const p = r.data.profile;
        setForm({ consent: !!p.consent, rules_accepted: !!p.rules_accepted, display_name: p.display_name || "", region: p.region || "", province_pays: p.province_pays || "", disciplines: p.disciplines || [], level: p.level || "", objective: p.objective || "", accepts_contact: !!p.accepts_contact });
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const loadDir = useCallback(() => {
    const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    memberApi.get(`/member/community/directory${qs ? "?" + qs : ""}`).then((r) => setDir(r.data.members || [])).catch(() => setDir([]));
  }, [filters]);

  useEffect(() => { loadProfile(); loadUnread(); }, [loadProfile, loadUnread]);
  useEffect(() => { if (tab === "annuaire") loadDir(); if (tab === "demandes") loadReqs(); if (tab === "groupes") { loadGroups(); loadMyMeetups(); } if (tab === "messages") { loadThreads(); setChat(null); loadUnread(); } }, [tab, loadDir, loadReqs, loadGroups, loadThreads, loadMyMeetups, loadUnread]);
  useEffect(() => { if (openGroup) loadMeetups(openGroup.id); }, [openGroup, loadMeetups]);

  const toggleDisc = (k) => setForm((f) => ({ ...f, disciplines: f.disciplines.includes(k) ? f.disciplines.filter((x) => x !== k) : [...f.disciplines, k] }));

  const save = async () => {
    if (!form.consent) { toast.error("Coche le consentement pour apparaître dans l'annuaire."); return; }
    if (!form.rules_accepted) { toast.error("Tu dois lire et accepter les règles du Cercle avant de rejoindre."); return; }
    setBusy(true);
    try {
      const r = await memberApi.put("/member/community/profile", form);
      setProfile(r.data.profile);
      toast.success("Profil communautaire enregistré — tu es maintenant visible.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'enregistrement."); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Retirer ton profil de l'annuaire ? Tu ne seras plus visible par les autres membres.")) return;
    setBusy(true);
    try {
      await memberApi.delete("/member/community/profile");
      setForm((f) => ({ ...f, consent: false }));
      setProfile((p) => (p ? { ...p, visible: false, consent: false } : p));
      toast.success("Profil retiré de l'annuaire.");
    } catch { toast.error("Échec du retrait."); }
    finally { setBusy(false); }
  };

  if (!loaded) return null;

  return (
    <section className="border border-[#C9A961]/40 bg-[#C9A961]/[0.05] p-6 mb-8 kz-fade-up" data-testid="member-community">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-[#C9A961] text-xs uppercase tracking-widest font-bold">Cercle K'Ien</h3>
        {profile?.visible && <span className="text-[10px] uppercase bg-emerald-900/40 text-emerald-300 px-2 py-0.5 rounded" data-testid="community-visible-badge">Visible</span>}
      </div>
      <p className="text-white text-lg font-bold mb-1">Trouver des partenaires d'entraînement</p>
      <p className="text-white/50 text-sm mb-4">Rejoins volontairement l'annuaire pour découvrir d'autres membres près de chez toi. Aucune adresse, téléphone ni courriel n'est jamais affiché. <a href="/regles-communaute" target="_blank" rel="noreferrer" className="text-[#C9A961] hover:underline" data-testid="community-rules-link">Voir les règles complètes ↗</a></p>

      <div className="flex gap-2 mb-5">
        <button data-testid="community-tab-annuaire" onClick={() => setTab("annuaire")} className={`px-4 py-2 rounded-md text-sm font-semibold ${tab === "annuaire" ? "bg-[#C9A961] text-black" : "border border-white/15 text-white/70"}`}>Annuaire</button>
        <button data-testid="community-tab-profil" onClick={() => setTab("profil")} className={`px-4 py-2 rounded-md text-sm font-semibold ${tab === "profil" ? "bg-[#C9A961] text-black" : "border border-white/15 text-white/70"}`}>Mon profil</button>
        <button data-testid="community-tab-demandes" onClick={() => setTab("demandes")} className={`px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-1.5 ${tab === "demandes" ? "bg-[#C9A961] text-black" : "border border-white/15 text-white/70"}`}>Demandes {reqs.incoming.filter((r) => r.status === "pending").length > 0 && <span className="bg-[#D90429] text-white text-[10px] px-1.5 rounded-full">{reqs.incoming.filter((r) => r.status === "pending").length}</span>}</button>
        <button data-testid="community-tab-groupes" onClick={() => setTab("groupes")} className={`px-4 py-2 rounded-md text-sm font-semibold ${tab === "groupes" ? "bg-[#C9A961] text-black" : "border border-white/15 text-white/70"}`}>Groupes & rencontres</button>
        <button data-testid="community-tab-messages" onClick={() => setTab("messages")} className={`px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-1.5 ${tab === "messages" ? "bg-[#C9A961] text-black" : "border border-white/15 text-white/70"}`}>Messages {unread > 0 && <span className="bg-[#D90429] text-white text-[10px] px-1.5 rounded-full" data-testid="messages-unread-badge">{unread}</span>}</button>
      </div>

      {tab === "profil" && (
        <div className="space-y-4" data-testid="community-profile-form">
          <div className="bg-black/30 border border-[#C9A961]/30 rounded-md p-4" data-testid="community-rules">
            <p className="text-[#C9A961] text-xs uppercase tracking-wider font-bold mb-2">📜 Règles du Cercle & confidentialité</p>
            <ul className="text-white/70 text-sm space-y-1.5 list-disc pl-5">
              <li><b>Respect avant tout</b> — aucune insulte, harcèlement, propos haineux ou sollicitation commerciale.</li>
              <li><b>Vie privée protégée</b> — ton adresse, ton téléphone et ton courriel ne sont <b>jamais</b> affichés. Seuls le pseudonyme, la région générale, les disciplines, le niveau et l'objectif que tu choisis sont visibles.</li>
              <li><b>Contact volontaire</b> — personne ne peut t'écrire sans que tu aies accepté sa demande de contact. Tu peux <b>bloquer</b> ou <b>signaler</b> en tout temps.</li>
              <li><b>Rencontres sécuritaires</b> — les rencontres se tiennent uniquement dans des <b>lieux publics</b>. Ne partage jamais d'adresse privée.</li>
              <li><b>Consentement retirable</b> — tu peux quitter le Cercle et retirer ton profil à tout moment.</li>
            </ul>
            <p className="text-white/40 text-xs mt-2">Conforme à la Loi 25 (protection des renseignements personnels au Québec). Consentement libre, clair et spécifique.</p>
          </div>
          <label className="flex items-start gap-3 bg-black/20 border border-white/10 p-3 rounded-md cursor-pointer">
            <input type="checkbox" data-testid="community-rules-accept" checked={form.rules_accepted} onChange={(e) => setForm({ ...form, rules_accepted: e.target.checked })} className="mt-1 accent-[#C9A961]" />
            <span className="text-sm text-white/80">J'ai lu et j'accepte les règles du Cercle K'Ien et la politique de confidentialité.</span>
          </label>
          <label className="flex items-start gap-3 bg-black/20 border border-white/10 p-3 rounded-md cursor-pointer">
            <input type="checkbox" data-testid="community-consent" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 accent-[#C9A961]" />
            <span className="text-sm text-white/80">J'accepte d'apparaître dans l'annuaire du Cercle K'Ien. Je comprends que seul mon profil volontaire (pseudonyme, région générale, disciplines, niveau, objectif) sera visible par les autres membres, et que je peux le retirer en tout temps.</span>
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Pseudonyme / prénom affiché"><input data-testid="community-display-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#C9A961] outline-none" placeholder="Ralph J." /></Field>
            <Field label="Niveau"><select data-testid="community-level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#C9A961] outline-none"><option value="">—</option>{Object.entries(LVL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
            <Field label="Région générale"><input data-testid="community-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#C9A961] outline-none" placeholder="Abitibi-Témiscamingue" /></Field>
            <Field label="Province / pays"><input data-testid="community-province" value={form.province_pays} onChange={(e) => setForm({ ...form, province_pays: e.target.value })} className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#C9A961] outline-none" placeholder="Québec, Canada" /></Field>
          </div>
          <Field label="Disciplines">
            <div className="flex flex-wrap gap-2">
              {Object.entries(DISC).map(([k, v]) => (
                <button key={k} type="button" data-testid={`community-disc-${k}`} onClick={() => toggleDisc(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${form.disciplines.includes(k) ? "bg-[#D90429] text-white" : "border border-white/15 text-white/60"}`}>{v}</button>
              ))}
            </div>
          </Field>
          <Field label="Objectif"><input data-testid="community-objective" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#C9A961] outline-none" placeholder="Trouver un partenaire d'entraînement" /></Field>
          <label className="flex items-center gap-3 text-sm text-white/80"><input type="checkbox" data-testid="community-accepts-contact" checked={form.accepts_contact} onChange={(e) => setForm({ ...form, accepts_contact: e.target.checked })} className="accent-[#C9A961]" /> J'accepte de recevoir des demandes de contact d'autres membres</label>
          <div className="flex gap-3 pt-1">
            <button onClick={save} disabled={busy} data-testid="community-save" className="px-5 py-2.5 rounded-md bg-[#C9A961] hover:bg-[#d8b96f] text-black font-bold disabled:opacity-50">{busy ? "…" : "Enregistrer mon profil"}</button>
            {profile?.visible && <button onClick={remove} disabled={busy} data-testid="community-remove" className="px-5 py-2.5 rounded-md border border-red-700 text-red-300 hover:bg-red-950/40 text-sm">Me retirer de l'annuaire</button>}
          </div>
        </div>
      )}

      {tab === "annuaire" && (
        <div data-testid="community-directory">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <input data-testid="dir-filter-region" value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })} className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#C9A961] outline-none" placeholder="Région" />
            <input data-testid="dir-filter-province" value={filters.province_pays} onChange={(e) => setFilters({ ...filters, province_pays: e.target.value })} className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#C9A961] outline-none" placeholder="Province / pays" />
            <select data-testid="dir-filter-discipline" value={filters.discipline} onChange={(e) => setFilters({ ...filters, discipline: e.target.value })} className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#C9A961] outline-none"><option value="">Toute discipline</option>{Object.entries(DISC).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            <select data-testid="dir-filter-level" value={filters.level} onChange={(e) => setFilters({ ...filters, level: e.target.value })} className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#C9A961] outline-none"><option value="">Tout niveau</option>{Object.entries(LVL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </div>
          {dir.length === 0 ? (
            <p className="text-white/40 text-sm py-8 text-center" data-testid="community-empty">Aucun membre visible pour ces critères. {!profile?.visible && "Active ton profil pour rejoindre la communauté."}</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {dir.map((m) => (
                <div key={m.id} className="border border-white/10 bg-black/20 p-4 rounded-md" data-testid={`community-card-${m.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-white font-bold">{m.display_name}</p>
                    <span className="text-[10px] uppercase text-white/40">{LVL[m.level] || ""}</span>
                  </div>
                  <p className="text-white/50 text-xs mt-0.5">{[m.region, m.province_pays].filter(Boolean).join(" · ")}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">{(m.disciplines || []).map((d) => <span key={d} className="text-[10px] bg-white/5 text-white/60 px-2 py-0.5 rounded">{DISC[d] || d}</span>)}</div>
                  {m.objective && <p className="text-white/60 text-sm mt-2 italic">« {m.objective} »</p>}
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`community-contact-${m.id}`} disabled={!m.accepts_contact} onClick={() => sendContact(m)} className="flex-1 px-3 py-2 rounded-md border border-[#C9A961]/50 text-[#C9A961] text-xs font-semibold hover:bg-[#C9A961]/10 disabled:opacity-40 disabled:cursor-not-allowed">{m.accepts_contact ? "Demande de contact" : "Ne reçoit pas de demandes"}</button>
                    <button data-testid={`community-report-${m.id}`} onClick={() => reportMember(m)} title="Signaler" className="px-2.5 py-2 rounded-md border border-white/15 text-white/50 text-xs hover:text-amber-400">⚑</button>
                    <button data-testid={`community-block-${m.id}`} onClick={() => blockMember(m)} title="Bloquer" className="px-2.5 py-2 rounded-md border border-white/15 text-white/50 text-xs hover:text-red-400">⛔</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "demandes" && (
        <div data-testid="community-requests">
          <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Reçues</p>
          {reqs.incoming.length === 0 ? <p className="text-white/40 text-sm mb-5">Aucune demande reçue.</p> : (
            <div className="space-y-2 mb-6">
              {reqs.incoming.map((r) => (
                <div key={r.id} className="border border-white/10 bg-black/20 rounded-md p-3 flex items-center justify-between" data-testid={`req-in-${r.id}`}>
                  <div><p className="text-white text-sm font-semibold">{r.from_name}</p>{r.message && <p className="text-white/50 text-xs mt-0.5">« {r.message} »</p>}</div>
                  {r.status === "pending" ? (
                    <div className="flex gap-2">
                      <button data-testid={`req-accept-${r.id}`} onClick={() => respondReq(r.id, "accept")} className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold">Accepter</button>
                      <button data-testid={`req-refuse-${r.id}`} onClick={() => respondReq(r.id, "refuse")} className="px-3 py-1.5 rounded-md border border-white/15 text-white/60 text-xs">Refuser</button>
                    </div>
                  ) : <span className={`text-xs font-semibold ${r.status === "accepted" ? "text-emerald-400" : "text-white/40"}`}>{r.status === "accepted" ? "✓ Accepté" : "Refusé"}</span>}
                </div>
              ))}
            </div>
          )}
          <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Envoyées</p>
          {reqs.outgoing.length === 0 ? <p className="text-white/40 text-sm">Aucune demande envoyée.</p> : (
            <div className="space-y-2">
              {reqs.outgoing.map((r) => (
                <div key={r.id} className="border border-white/10 bg-black/20 rounded-md p-3 flex items-center justify-between" data-testid={`req-out-${r.id}`}>
                  <p className="text-white text-sm">{r.to_name}</p>
                  <span className={`text-xs font-semibold ${r.status === "accepted" ? "text-emerald-400" : r.status === "refused" ? "text-white/40" : "text-amber-400"}`}>{r.status === "accepted" ? "✓ Accepté — vous êtes connectés" : r.status === "refused" ? "Refusé" : "En attente"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "groupes" && (
        <div data-testid="community-groups">
          {myMeetups.length > 0 && !openGroup && (
            <div className="border border-[#C9A961]/40 bg-[#C9A961]/[0.06] rounded-lg p-4 mb-5" data-testid="my-meetups">
              <p className="text-[#C9A961] text-xs uppercase tracking-wider font-bold mb-2">📅 Mes rencontres à venir</p>
              <div className="space-y-2">
                {myMeetups.map((m) => (
                  <div key={m.id} className="text-sm text-white/80" data-testid={`my-meetup-${m.id}`}>
                    <b className="text-white">{m.title}</b> — 📍 {m.venue_public} · 📅 {m.date}
                    {m.group_name && <span className="text-white/40 text-xs"> · {m.group_name}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!openGroup ? (
            <div className="grid sm:grid-cols-2 gap-3">
              {groups.map((g) => (
                <div key={g.id} className="border border-white/10 bg-black/20 rounded-md p-4" data-testid={`group-${g.id}`}>
                  <p className="text-white font-bold">{g.name}</p>
                  <p className="text-white/40 text-xs mt-0.5">{g.member_count} membre(s)</p>
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`group-open-${g.id}`} onClick={() => setOpenGroup(g)} className="flex-1 px-3 py-2 rounded-md border border-white/15 text-white/70 text-xs hover:bg-white/5">Voir les rencontres</button>
                    <button data-testid={`group-toggle-${g.id}`} onClick={() => toggleGroup(g)} className={`px-3 py-2 rounded-md text-xs font-semibold ${g.joined ? "border border-white/15 text-white/60" : "bg-[#C9A961] text-black"}`}>{g.joined ? "Quitter" : "Rejoindre"}</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div data-testid="group-detail">
              <button onClick={() => setOpenGroup(null)} className="text-white/50 text-sm mb-3 hover:text-white">← Retour aux groupes</button>
              <p className="text-white text-lg font-bold mb-4">{openGroup.name}</p>
              <div className="border border-white/10 bg-white/[0.02] rounded-lg p-4 mb-5">
                <p className="text-[#C9A961] text-xs uppercase tracking-wider mb-3">Organiser une rencontre (lieu public uniquement)</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="meetup-title" value={newMeetup.title} onChange={(e) => setNewMeetup({ ...newMeetup, title: e.target.value })} placeholder="Titre (ex. Open mat BJJ)" className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#C9A961]" />
                  <input data-testid="meetup-venue" value={newMeetup.venue_public} onChange={(e) => setNewMeetup({ ...newMeetup, venue_public: e.target.value })} placeholder="Lieu PUBLIC (parc, gym, dojo…)" className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#C9A961]" />
                  <input data-testid="meetup-date" value={newMeetup.date} onChange={(e) => setNewMeetup({ ...newMeetup, date: e.target.value })} placeholder="Date & heure (ex. 2026-09-01 10:00)" className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#C9A961]" />
                  <select data-testid="meetup-level" value={newMeetup.level} onChange={(e) => setNewMeetup({ ...newMeetup, level: e.target.value })} className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white outline-none focus:border-[#C9A961]"><option value="">Niveau (optionnel)</option>{Object.entries(LVL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
                </div>
                <input data-testid="meetup-rules" value={newMeetup.rules} onChange={(e) => setNewMeetup({ ...newMeetup, rules: e.target.value })} placeholder="Règles de sécurité / à apporter" className="w-full mt-3 bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#C9A961]" />
                <button data-testid="meetup-create" onClick={createMeetup} className="mt-3 px-4 py-2 rounded-md bg-[#C9A961] text-black text-sm font-bold">Créer la rencontre</button>
              </div>
              {meetups.length === 0 ? <p className="text-white/40 text-sm text-center py-6">Aucune rencontre planifiée. Sois le premier à en organiser une !</p> : (
                <div className="space-y-3">
                  {meetups.map((mt) => (
                    <div key={mt.id} className="border border-white/10 bg-black/20 rounded-md p-4" data-testid={`meetup-${mt.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-white font-bold">{mt.title}</p>
                        <span className="text-white/40 text-xs">{mt.rsvp_count} participant(s)</span>
                      </div>
                      <p className="text-white/60 text-sm mt-1">📍 {mt.venue_public} · 📅 {mt.date}{mt.level ? ` · ${LVL[mt.level]}` : ""}</p>
                      {mt.rules && <p className="text-white/40 text-xs mt-1">⚠️ {mt.rules}</p>}
                      <p className="text-white/30 text-[11px] mt-1">Organisé par {mt.created_by_name}</p>
                      <button data-testid={`meetup-rsvp-${mt.id}`} onClick={() => rsvp(mt.id, !mt.im_going)} className={`mt-3 px-4 py-2 rounded-md text-xs font-semibold ${mt.im_going ? "bg-emerald-600 text-white" : "border border-[#C9A961]/50 text-[#C9A961] hover:bg-[#C9A961]/10"}`}>{mt.im_going ? "✓ J'y serai" : "Je participe"}</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "messages" && (
        <div data-testid="community-messages">
          {!chat ? (
            threads.length === 0 ? (
              <p className="text-white/40 text-sm text-center py-8" data-testid="messages-empty">Aucune conversation. Une messagerie s'ouvre dès qu'une demande de contact est <b>acceptée des deux côtés</b>.</p>
            ) : (
              <div className="space-y-2">
                {threads.map((t) => (
                  <button key={t.other_member_id} data-testid={`thread-${t.other_member_id}`} onClick={() => openChat(t)} className="w-full text-left border border-white/10 bg-black/20 rounded-md p-3 hover:bg-white/5 flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-semibold">{t.display_name}</p>
                      <p className="text-white/50 text-xs mt-0.5 truncate max-w-md">{t.last_message || "Démarrer la conversation…"}</p>
                    </div>
                    {t.unread > 0 && <span className="bg-[#D90429] text-white text-[10px] px-2 py-0.5 rounded-full">{t.unread}</span>}
                  </button>
                ))}
              </div>
            )
          ) : (
            <div data-testid="chat-view">
              <button onClick={() => { setChat(null); loadThreads(); }} className="text-white/50 text-sm mb-3 hover:text-white">← Retour aux conversations</button>
              <p className="text-white font-bold mb-3">{chat.display_name}</p>
              <div className="border border-white/10 bg-black/30 rounded-lg p-4 h-72 overflow-y-auto space-y-2 mb-3" data-testid="chat-messages">
                {messages.length === 0 && <p className="text-white/30 text-sm text-center py-8">Dis bonjour 👋</p>}
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-lg text-sm ${m.mine ? "bg-[#D90429] text-white" : "bg-white/10 text-white/90"}`}>{m.body}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input data-testid="chat-input" value={msgInput} onChange={(e) => setMsgInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendMsg(); }} placeholder="Écris un message…" className="flex-1 bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#C9A961]" />
                <button data-testid="chat-send" onClick={sendMsg} className="px-5 py-2 rounded-md bg-[#C9A961] text-black font-bold">Envoyer</button>
              </div>
            </div>
          )}
        </div>
      )}

    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-white/40 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

